import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';
import { useModuleApp } from '../services/features';
import { isAoAdmin } from './AoSociete';
import { useConfirm, confirmErpia, alertErpia } from './ConfirmDialog';
import { useTeam, MentionArea, notifyMentions, renderMentions } from './Mentions';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

// ── Brique Ressources — vue manager (contrat back /resources) ────────────────
//   GET  /resources                        → liste { id, nom, prenom, client, mission_titre, besoin_id, date_debut, date_fin, taux_activite, statut, user_id?, user_username? }
//   POST /resources                        → création (user_id lie le compte et pose le rôle ressource)
//   PUT  /resources/:id                    → édition (user_id: null délie)
//   GET  /resources/:id/cra?month=YYYY-MM  → { days:{ISO:1|0.5}, total, status: draft|submitted|signature_en_cours|client_validated|valide_interne, locked }
//   PUT  /resources/:id/cra/status?month=  → { status: 'client_validated' | 'valide_interne' } (verrouille) | { status: 'draft' } (déverrouille)
//   POST /resources/:id/cra/signature?month= → envoie le CRA au client pour
//        signature électronique signd (status → signature_en_cours ; le
//        webhook signd le passera à client_validated) — route tolérée absente.
//
//   Circuit de validation (le mois se valide généralement LE MOIS SUIVANT,
//   donc aucun geste n'est réservé au mois courant) :
//     soumis → (signature client signd OU validation manuelle) → validé client
//            → validé compta (la donnée que consommera Billy, paie/compta)
//            → facture (POST /factures/from-cra) → envoi au client.

const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const GHOST_BTN = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const PRIMARY_BTN = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const INPUT = { boxSizing: 'border-box', width: '100%', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '10px 12px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };
const SEG = (on) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 13px', borderRadius: '99px', border: 'none', background: on ? 'var(--accent)' : 'transparent', color: on ? 'var(--accent-fg)' : 'var(--text-2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap' });
const LBL = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
// Les données qui se relisent caractère par caractère (IBAN, n° AVS, NPA).
const MONO = { fontFamily: "'IBM Plex Mono',monospace" };
const Chip = ({ kind = 'mut', title, style, children }) => (
  <span className={`src-chip src-chip--${kind}`} title={title} style={style}>{children}</span>
);

const CRA_CHIP = {
  valide_interne:     { l: 'Validé — prêt compta', k: 'ok' },
  client_validated:   { l: 'Validé client',      k: 'ok' },
  signature_en_cours: { l: 'Signature client en cours', k: 'warn' },
  submitted:          { l: 'CRA reçu — à valider', k: 'warn' },
  draft:              { l: 'En saisie',          k: 'mut' },
};
const craChip = (s) => CRA_CHIP[s] || { l: 'Sans CRA', k: 'mut' };
const initialsOf = (r) => {
  const n = typeof r === 'string' ? r : [r?.prenom, r?.nom].filter(Boolean).join(' ');
  return String(n || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
};
const nameOf = (r) => [r?.prenom, r?.nom].filter(Boolean).join(' ').trim() || r?.nom || '—';
const mKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const mLabel = (key) => { const [y, m] = key.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
const shiftMonth = (key, delta) => { const [y, m] = key.split('-').map(Number); return mKey(new Date(y, m - 1 + delta, 1)); };
const monthDays = (key) => { const [y, m] = key.split('-').map(Number); return new Date(y, m, 0).getDate(); };
const firstDayPad = (key) => { const [y, m] = key.split('-').map(Number); return (new Date(y, m - 1, 1).getDay() + 6) % 7; };
const isWeekend = (key, d) => { const [y, m] = key.split('-').map(Number); const wd = new Date(y, m - 1, d).getDay(); return wd === 0 || wd === 6; };
const isoOf = (key, d) => `${key}-${String(d).padStart(2, '0')}`;
const fmtDate = (iso) => { if (!iso) return ''; const t = new Date(iso); return Number.isNaN(t.getTime()) ? iso : t.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }); };

// Fenêtre d'immeuble : allumée (accent) = CRA validé, ambre = à suivre, éteinte = sans CRA.
const winStyle = (cra) => {
  const base = { display: 'block', width: '100%', aspectRatio: '1/1.35', borderRadius: '2.5px' };
  if (cra === 'client_validated' || cra === 'valide_interne') return { ...base, background: 'var(--accent)' };
  if (cra) return { ...base, background: 'rgba(234,160,30,0.55)' };
  return { ...base, background: 'var(--code-bg)', border: '1px solid var(--hair)' };
};

export default function Ressources({ showNotification, onOpenFacture, ficheInitiale }) {
  const [resources, setResources] = useState(undefined); // undefined = chargement, null = route absente
  const [craMap, setCraMap] = useState({});              // id → { status, locked, total }
  const [scope, setScope] = useState('dash');            // 'dash' | 'liste'
  const [viewMode, setViewMode] = useState('agences');   // 'agences' (par client) | 'liste' | 'equipe'
  const [rq, setRq] = useState('');
  const [openClient, setOpenClient] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerTab, setDrawerTab] = useState('synthese');
  // /ressources/:id — on arrive DIRECTEMENT sur une fiche (lien du CRM,
  // notification, favori). L'onglet « Administratif » est celui qu'on vient
  // voir depuis le CRM : c'est là que vivent le formulaire et les
  // informations qu'il remplit.
  useEffect(() => {
    if (ficheInitiale == null || ficheInitiale === '') return;
    setDrawerId(String(ficheInitiale));
    setDrawerTab('admin');
  }, [ficheInitiale]);
  const [detailId, setDetailId] = useState(null);         // vue détaillée pleine page (⤢)
  const [showAdd, setShowAdd] = useState(false);
  const month = mKey(new Date());
  const admin = isAoAdmin(APIService.getCurrentUser());
  const crmOn = useModuleApp('crm');
  const { confirm, confirmElement } = useConfirm();

  // Facturer un CRA validé client : POST /factures/from-cra. Les 409 du back
  // sont des messages métier prêts à afficher tels quels ; « déjà facturé »
  // propose le doublon volontaire (dupliquer: true).
  const facturerCra = useCallback(async (resourceId, mois, dupliquer = false) => {
    try {
      const r = await fetch(`${BASE}/factures/from-cra`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ resource_id: resourceId, month: mois, ...(dupliquer ? { dupliquer: true } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501) { showNotification?.('La facturation depuis le CRA arrive côté serveur.', 'error'); return false; }
      if (r.status === 409) return { conflit: j.error || j.message || 'Conflit de facturation.' };
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const num = j.data?.numero || j.data?.number || '';
      const fid = j.data?.id ?? null;
      // Accès direct : le toast emmène sur la fiche facture (CRM → Factures)
      // pour l'éditer, l'envoyer, suivre son état.
      showNotification?.(`Facture ${num ? `${num} ` : ''}créée${j.jours != null ? ` : ${j.jours} j${j.tjm != null ? ` × ${j.tjm}` : ''}` : ''} — visible dans CRM → Factures.`, 'success',
        fid != null && onOpenFacture ? { duration: 9000, action: { label: 'Ouvrir la facture', onClick: () => onOpenFacture(fid) } } : {});
      return { ok: true, factureId: fid, numero: num };
    } catch (e) { showNotification?.(`Facturation impossible : ${e.message}`, 'error'); return false; }
  }, [showNotification, onOpenFacture]);

  // Envoi de la facture au client (email avec le PDF) — action SORTANTE,
  // toujours confirmée. Route tolérée absente : rien ne part.
  const envoyerFacture = useCallback(async (factureId, numero) => {
    const ok = await confirm({
      title: 'Envoyer la facture au client',
      message: `La facture ${numero || ''} vient d'être créée — l'envoyer au client par email ?`,
      detail: 'Le client reçoit le PDF de la facture. Vous pouvez aussi l’envoyer plus tard depuis CRM → Factures.',
      confirmLabel: 'Envoyer au client', cancelLabel: 'Plus tard', tone: 'primary',
    });
    if (!ok) return;
    try {
      const r = await fetch(`${BASE}/factures/${factureId}/envoyer`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      // 409 (payée, pas de contact « Reçoit les factures »…) et 502 (email en
      // échec, réessayable) portent un message affichable tel quel.
      if ((r.status === 404 || r.status === 405 || r.status === 501) && !j.error && !j.message) {
        showNotification?.('L’envoi par email arrive côté serveur — rien n’est parti, la facture reste dans CRM → Factures.', 'error');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || `Facture ${numero || ''} envoyée au client.`, 'success');
    } catch (e) { showNotification?.(`Envoi impossible : ${e.message}`, 'error'); }
  }, [confirm, showNotification]);

  const facturer = useCallback(async (resourceId, mois) => {
    const res = await facturerCra(resourceId, mois);
    if (res && res.conflit) {
      if (/d[ée]j[àa] factur/i.test(res.conflit)) {
        if (await confirm({ title: 'Déjà facturé', message: res.conflit, detail: 'Créer quand même une seconde facture pour ce CRA ?', confirmLabel: 'Facturer quand même', tone: 'primary' })) {
          const res2 = await facturerCra(resourceId, mois, true);
          if (res2 && res2.ok && res2.factureId != null) await envoyerFacture(res2.factureId, res2.numero);
        }
      } else showNotification?.(res.conflit, 'error');
      return;
    }
    // Facture créée : on propose l'envoi dans la foulée — le geste demandé
    // (« générer la facture… et on pourra l'envoyer au client »).
    if (res && res.ok && res.factureId != null) await envoyerFacture(res.factureId, res.numero);
  }, [facturerCra, envoyerFacture, confirm, showNotification]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/resources`, { headers: authH() });
      if (r.status === 404) { setResources(null); return; }
      if (!r.ok) { setResources(prev => prev === undefined ? null : prev); return; }
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j.resources || (Array.isArray(j) ? j : []);
      setResources(Array.isArray(arr) ? arr : []);
    } catch { setResources(prev => prev === undefined ? null : prev); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // CRA du mois courant par ressource (fenêtres, KPIs, file À valider) — tolérant.
  useEffect(() => {
    if (!Array.isArray(resources) || resources.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(resources.map(async (r) => {
        try {
          const rr = await fetch(`${BASE}/resources/${r.id}/cra?month=${month}`, { headers: authH() });
          if (!rr.ok) return [r.id, null];
          const j = await rr.json().catch(() => ({}));
          const d = j.data || j;
          return [r.id, { status: d.status || 'draft', locked: !!d.locked, total: d.total ?? Object.values(d.days || {}).reduce((s, v) => s + (Number(v) || 0), 0) }];
        } catch { return [r.id, null]; }
      }));
      if (!cancelled) setCraMap(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [resources, month]);

  const all = useMemo(() => (Array.isArray(resources) ? resources : []), [resources]);
  const q = rq.trim().toLowerCase();
  const filtered = all.filter(r => !q || `${nameOf(r)} ${r.mission_titre || ''} ${r.client || ''} ${r.user_username || ''}`.toLowerCase().includes(q));
  const byClient = useMemo(() => {
    const m = new Map();
    for (const r of filtered) { const c = r.client || 'Sans client'; if (!m.has(c)) m.set(c, []); m.get(c).push(r); }
    return [...m.entries()];
  }, [filtered]);

  const craOf = (r) => craMap[r.id]?.status ?? null;
  const kpi = {
    total: all.length,
    submitted: all.filter(r => craOf(r) === 'submitted').length,
    validated: all.filter(r => craOf(r) === 'client_validated').length,
    draft: all.filter(r => craOf(r) === 'draft').length,
  };
  const pendingRows = all.filter(r => craOf(r) === 'submitted');

  // Changement d'état du CRA (validation client, validation compta,
  // déverrouillage) — sur le MOIS AFFICHÉ, pas seulement le mois courant :
  // dans la vraie vie, le CRA d'août se valide début septembre.
  const MSG_STATUT = {
    client_validated: (r) => `CRA de ${nameOf(r)} validé (client) — mois verrouillé.`,
    valide_interne: (r) => `CRA de ${nameOf(r)} validé pour la compta — prêt pour la facturation.`,
    draft: (r) => `CRA de ${nameOf(r)} déverrouillé.`,
  };
  const setCraStatus = async (r, status, mois = month) => {
    try {
      const rr = await fetch(`${BASE}/resources/${r.id}/cra/status?month=${mois}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ status }) });
      const j = await rr.json().catch(() => ({}));
      if (status === 'valide_interne' && (rr.status === 400 || rr.status === 404 || rr.status === 422)) {
        // Serveur pas encore au fait de la validation compta : on l'annonce,
        // rien ne change à l'écran.
        showNotification?.(j.error || j.message || 'La validation compta arrive côté serveur — l’état n’a pas changé.', 'error');
        return false;
      }
      if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
      showNotification?.(j.message || (MSG_STATUT[status] ? MSG_STATUT[status](r) : 'État du CRA mis à jour.'), 'success');
      if (mois === month) setCraMap(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), status, locked: status !== 'draft' } }));
      return true;
    } catch (e) { showNotification?.('Erreur validation CRA : ' + e.message, 'error'); return false; }
  };

  // Signature électronique du CRA par le client (signd) — action SORTANTE
  // (email au client), toujours confirmée. Route tolérée absente.
  const demanderSignatureCra = async (r, mois) => {
    const ok = await confirmErpia({
      title: 'Faire signer le CRA par le client',
      message: `Envoyer le CRA ${mLabel(mois)} de ${nameOf(r)} au client pour signature électronique (signd) ?`,
      detail: 'Le client reçoit un email avec le relevé à signer. À la signature, le CRA passera automatiquement en « Validé client ».',
      confirmLabel: 'Envoyer pour signature', cancelLabel: 'Annuler', tone: 'primary',
    });
    if (!ok) return false;
    try {
      const rr = await fetch(`${BASE}/resources/${r.id}/cra/signature?month=${mois}`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      const j = await rr.json().catch(() => ({}));
      // Contrat serveur : les 404/409 portent un message affichable tel quel
      // (ex. « désignez le rôle clé Valide les temps dans l'organigramme du
      // client ») — seule une réponse SANS corps d'erreur signale une route
      // pas encore servie.
      if ((rr.status === 404 || rr.status === 405 || rr.status === 501) && !j.error && !j.message) {
        showNotification?.('La signature du CRA arrive côté serveur — rien n’a été envoyé.', 'error');
        return false;
      }
      if (!rr.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${rr.status}`);
      showNotification?.(j.message || `CRA envoyé au client pour signature — l’état se mettra à jour dès la signature.`, 'success');
      if (mois === month) setCraMap(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), status: 'signature_en_cours' } }));
      return true;
    } catch (e) { showNotification?.(`Demande de signature impossible : ${e.message}`, 'error'); return false; }
  };

  const sel = all.find(r => String(r.id) === String(drawerId)) || null;
  const detail = all.find(r => String(r.id) === String(detailId)) || null;

  const ResRow = ({ r }) => {
    const cra = craChip(craOf(r));
    return (
      <div onClick={() => { setDrawerId(r.id); setDrawerTab('synthese'); }} className="src-row-hover"
        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 18px', borderBottom: '1px solid var(--hair)', cursor: 'pointer', flexWrap: 'wrap' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, flex: 'none' }}>{initialsOf(r)}</div>
        <div style={{ flex: 1.4, minWidth: '160px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(r)}</div>
          <div style={{ marginTop: '1px', fontSize: '12px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.mission_titre || '—'}</div>
        </div>
        {r.user_username
          ? <Chip kind="mut" title={`Compte lié : ${r.user_username}`}><BI icon="person-check" style={{ fontSize: '10px' }} /> {r.user_username}</Chip>
          : <Chip kind="mut" title="Aucun compte utilisateur lié" style={{ opacity: 0.6 }}>sans compte</Chip>}
        {r.taux_activite != null && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-3)', flex: 'none', width: '44px', textAlign: 'right' }}>{r.taux_activite}%</span>}
        <Chip kind={cra.k} title="CRA du mois courant">{cra.l}</Chip>
      </div>
    );
  };

  if (resources === null) {
    return (
      <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
        <h1 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '26px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>Ressources</h1>
        <div style={{ ...CARD, marginTop: '20px', padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'var(--code-bg)', color: 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BI icon="person-gear" style={{ fontSize: '22px' }} /></div>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Module indisponible</div>
          <div style={{ maxWidth: '440px', fontSize: '13px', lineHeight: 1.55, color: 'var(--text-2)' }}>La route <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px' }}>GET /resources</code> ne répond pas — vérifiez le déploiement backend.</div>
          <button onClick={load} className="src-row-hover" style={{ ...GHOST_BTN, marginTop: '4px' }}><BI icon="arrow-repeat" /> Réessayer</button>
        </div>
      </div>
    );
  }

  // Vue détaillée pleine page (⤢ depuis le drawer) — remplace l'écran liste
  if (detail) {
    return <ResourceDetail r={detail} onBack={() => setDetailId(null)} showNotification={showNotification} onSaved={load} />;
  }

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '26px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>Ressources</h1>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '2px 9px', borderRadius: '6px' }}>{all.length}</span>
          </div>
          <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-3)' }}>Consultants en mission — CRA, validation client, comptes liés.</div>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px', borderRadius: '99px', background: 'var(--code-bg)', border: '1px solid var(--border)', marginLeft: '4px' }}>
          <button onClick={() => setScope('dash')} style={SEG(scope === 'dash')}><BI icon="grid-1x2" style={{ fontSize: '11px' }} />Dashboard</button>
          <button onClick={() => setScope('liste')} style={SEG(scope === 'liste')}>Ressources · {all.length}</button>
          {admin && <button onClick={() => setScope('cra')} style={SEG(scope === 'cra')}><BI icon="calendar-check" style={{ fontSize: '11px' }} />CRA du mois</button>}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={load} className="src-row-hover" style={GHOST_BTN}><BI icon="arrow-repeat" /> Rafraîchir</button>
        <button onClick={() => setShowAdd(true)} style={{ ...PRIMARY_BTN, padding: '9px 14px' }}><BI icon="plus-lg" style={{ fontSize: '12px' }} />Ajouter une ressource</button>
      </div>

      {resources === undefined && (
        <div style={{ ...CARD, marginTop: '20px', padding: '32px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
          <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement des ressources…
        </div>
      )}

      {/* ── Dashboard ── */}
      {Array.isArray(resources) && scope === 'dash' && (
        <>
          <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: '12px' }}>
            {[
              { v: kpi.total, t: 'En mission', s: 'ressources actives' },
              { v: kpi.submitted, t: 'À valider', s: 'CRA soumis au client', dot: kpi.submitted > 0 },
              { v: kpi.validated, t: 'Validés', s: mLabel(month) },
              { v: kpi.draft, t: 'CRA en saisie', s: mLabel(month) },
            ].map((k) => (
              <div key={k.t} style={{ ...CARD, padding: '15px 17px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '30px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--text-strong)', lineHeight: 1 }}>{k.v}</span>
                  {k.dot && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--brand)' }} />}
                </div>
                <div style={{ marginTop: '7px', fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>{k.t}</div>
                <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--text-3)', textTransform: 'capitalize' }}>{k.s}</div>
              </div>
            ))}
          </div>

          {/* File « À valider » : CRA soumis → validation client (verrouille) */}
          <div style={{ ...CARD, marginTop: '20px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', borderBottom: '1px solid var(--hair)' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>À valider</span>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '1px 7px', borderRadius: '6px' }}>{pendingRows.length}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>CRA soumis — enregistrer la validation client verrouille le mois</span>
            </div>
            {pendingRows.length === 0 ? (
              <div style={{ padding: '18px', fontSize: '13px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '9px' }}><BI icon="check-lg" /> Tout est validé — rien en attente.</div>
            ) : pendingRows.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'var(--code-bg)', color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="calendar3" style={{ fontSize: '14px' }} /></div>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>CRA {mLabel(month)} — {nameOf(r)}</div>
                  <div style={{ marginTop: '1px', fontSize: '12px', color: 'var(--text-3)' }}>{[r.mission_titre, r.client].filter(Boolean).join(' · ')}{craMap[r.id]?.total != null ? ` · ${craMap[r.id].total} j` : ''}</div>
                </div>
                <button onClick={() => { setDrawerId(r.id); setDrawerTab('cra'); }} className="src-row-hover" style={GHOST_BTN}><BI icon="eye" /> Voir le CRA</button>
                <button onClick={() => demanderSignatureCra(r, month)} className="src-row-hover" style={GHOST_BTN} title="Le client reçoit le relevé à signer (signd) — validation automatique à la signature.">
                  <BI icon="pen" /> Faire signer (signd)
                </button>
                <button onClick={() => setCraStatus(r, 'client_validated')} style={{ ...PRIMARY_BTN, padding: '8px 13px', fontSize: '12.5px' }}><BI icon="check-lg" /> Valider (client)</button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Vue ressources ── */}
      {Array.isArray(resources) && scope === 'liste' && (
        <>
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px', display: 'flex', alignItems: 'center', gap: '9px', padding: '0 13px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--input-bg)' }}>
              <BI icon="search" style={{ color: 'var(--text-faint)', fontSize: '13px' }} />
              <input value={rq} onChange={e => setRq(e.target.value)} placeholder="Rechercher — nom, mission, client…"
                style={{ flex: 1, minWidth: 0, padding: '11px 0', border: 'none', background: 'transparent', color: 'var(--text-strong)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }} />
            </div>
            {[['agences', 'buildings', 'Clients'], ['liste', 'list-ul', 'Liste'], ['equipe', 'people', 'Équipe']].map(([v, ic, l]) => (
              <button key={v} onClick={() => { setViewMode(v); setOpenClient(null); }} style={{ ...SEG(viewMode === v), border: viewMode === v ? '1px solid transparent' : '1px solid var(--input-border)', borderRadius: '8px', background: viewMode === v ? 'var(--accent)' : 'var(--ghost-bg)' }}><BI icon={ic} style={{ fontSize: '12px' }} />{l}</button>
            ))}
          </div>

          {/* Immeubles par client */}
          {viewMode === 'agences' && !openClient && (
            <>
              <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(248px,1fr))', gap: '16px' }}>
                {byClient.map(([name, rows], i) => {
                  const warn = rows.filter(r => craOf(r) && craOf(r) !== 'client_validated').length;
                  return (
                    <div key={name} onClick={() => setOpenClient(name)}
                      style={{ ...CARD, cursor: 'pointer', overflow: 'hidden', transition: 'transform .15s ease, box-shadow .15s ease', animation: 'agencyIn .35s ease both', animationDelay: `${i * 70}ms` }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; }} onMouseLeave={e => { e.currentTarget.style.transform = ''; }}>
                      <div style={{ padding: '20px 20px 0' }}>
                        <div style={{ height: '5px', background: 'var(--accent)', borderRadius: '3px 3px 0 0', margin: '0 30px' }} />
                        <div style={{ margin: '0 16px', background: 'var(--panel)', border: '1px solid var(--border)', borderBottom: 'none', borderRadius: '5px 5px 0 0', padding: '13px 13px 0' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: '5px' }}>
                            {rows.map((r, k) => <span key={k} title={`${nameOf(r)} — ${craChip(craOf(r)).l}`} style={winStyle(craOf(r))} />)}
                            {Array.from({ length: Math.max(0, 12 - rows.length) }, (_, k) => <span key={`e${k}`} style={{ display: 'block', width: '100%', aspectRatio: '1/1.35', borderRadius: '2.5px', background: 'var(--code-bg)', border: '1px solid var(--hair)', opacity: 0.5 }} />)}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '11px' }}><span style={{ width: '16px', height: '14px', background: 'var(--brand)', borderRadius: '3px 3px 0 0' }} /></div>
                        </div>
                      </div>
                      <div style={{ padding: '13px 16px 15px', borderTop: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: '9px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '16.5px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{name}</div>
                          <div style={{ marginTop: '2px', fontSize: '11.5px', color: 'var(--text-3)' }}>{warn ? `${warn} CRA à suivre` : 'Tout est à jour'}</div>
                        </div>
                        <Chip kind="mut" style={{ fontFamily: "'IBM Plex Mono',monospace" }}>{rows.length}</Chip>
                        <BI icon="chevron-right" style={{ color: 'var(--text-faint)', fontSize: '12px', flex: 'none' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', fontSize: '11.5px', color: 'var(--text-3)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '11px', height: '11px', borderRadius: '2.5px', background: 'var(--accent)' }} />Fenêtre allumée — CRA validé</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '11px', height: '11px', borderRadius: '2.5px', background: 'rgba(234,160,30,0.55)' }} />CRA à suivre</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '11px', height: '11px', borderRadius: '2.5px', background: 'var(--code-bg)', border: '1px solid var(--hair)' }} />Sans CRA</span>
              </div>
            </>
          )}

          {viewMode === 'agences' && openClient && (() => {
            const rows = (byClient.find(([n]) => n === openClient) || [null, []])[1];
            return (
              <div style={{ ...CARD, marginTop: '16px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '13px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
                  <button onClick={() => setOpenClient(null)} title="Tous les clients" className="src-row-hover" style={{ ...GHOST_BTN, width: '32px', height: '32px', padding: 0, justifyContent: 'center' }}><BI icon="arrow-left" style={{ fontSize: '13px' }} /></button>
                  <BI icon="buildings" style={{ color: 'var(--text-2)', fontSize: '15px' }} />
                  <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '17px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{openClient}</span>
                  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '1px 7px', borderRadius: '6px' }}>{rows.length}</span>
                </div>
                {rows.map((r, i) => <div key={r.id ?? i} style={{ animation: 'rowCascade .3s ease both', animationDelay: `${i * 60}ms` }}><ResRow r={r} /></div>)}
              </div>
            );
          })()}

          {viewMode === 'liste' && (
            <div style={{ ...CARD, marginTop: '14px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', borderBottom: '1px solid var(--hair)' }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Ressources</span>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '1px 7px', borderRadius: '6px' }}>{filtered.length}</span>
                <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>cliquer une ligne pour ouvrir la fiche</span>
              </div>
              {byClient.map(([name, rows]) => (
                <React.Fragment key={name}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '8px 18px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                    <BI icon="buildings" style={{ color: 'var(--text-3)', fontSize: '12px' }} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-strong)' }}>{name}</span>
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '1px 6px', borderRadius: '5px' }}>{rows.length}</span>
                  </div>
                  {rows.map((r, i) => <ResRow key={r.id ?? i} r={r} />)}
                </React.Fragment>
              ))}
              {filtered.length === 0 && <div style={{ padding: '22px 18px', fontSize: '13px', color: 'var(--text-3)' }}>Aucune ressource ne correspond à la recherche.</div>}
            </div>
          )}

          {viewMode === 'equipe' && (
            <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(196px,1fr))', gap: '14px' }}>
              {filtered.map((r, i) => {
                const cra = craChip(craOf(r));
                return (
                  <div key={r.id ?? i} onClick={() => { setDrawerId(r.id); setDrawerTab('synthese'); }}
                    style={{ ...CARD, padding: '18px 14px 15px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'transform .15s ease', animation: 'agencyIn .3s ease both', animationDelay: `${i * 50}ms` }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }} onMouseLeave={e => { e.currentTarget.style.transform = ''; }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '16px', fontWeight: 600 }}>{initialsOf(r)}</div>
                    <div style={{ marginTop: '11px', fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)', textAlign: 'center', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(r)}</div>
                    <div style={{ marginTop: '2px', fontSize: '11.5px', color: 'var(--text-3)', textAlign: 'center', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.mission_titre || '—'}</div>
                    <div style={{ marginTop: '4px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', fontWeight: 600, letterSpacing: '0.05em', color: 'var(--text-faint)', textAlign: 'center' }}>{r.client || ''}</div>
                    <div style={{ marginTop: '11px', display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      {r.taux_activite != null && <Chip kind="mut" style={{ fontFamily: "'IBM Plex Mono',monospace" }}>{r.taux_activite}%</Chip>}
                      <Chip kind={cra.k}>{cra.l}</Chip>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <div style={{ gridColumn: '1/-1', padding: '22px 18px', fontSize: '13px', color: 'var(--text-3)' }}>Aucune ressource ne correspond à la recherche.</div>}
            </div>
          )}
        </>
      )}

      {admin && scope === 'cra' && (
        <CraOverview showNotification={showNotification} crmOn={crmOn} onFacturer={facturer}
          onSetStatus={(row, status, mois) => setCraStatus({ ...row, id: row.resource_id ?? row.id }, status, mois)}
          onSignature={(row, mois) => demanderSignatureCra({ ...row, id: row.resource_id ?? row.id }, mois)}
          onOpenResource={(id) => { setDrawerTab('cra'); setDrawerId(id); }} />
      )}

      {confirmElement}
      {sel && <ResourceDrawer r={sel} tab={drawerTab} setTab={setDrawerTab} onClose={() => setDrawerId(null)}
        onExpand={(res) => { setDrawerId(null); setDetailId(res.id); }}
        craNow={craMap[sel.id]} onSetStatus={(status, mois) => setCraStatus(sel, status, mois)}
        onSignature={(mois) => demanderSignatureCra(sel, mois)}
        showNotification={showNotification} onSaved={load} crmOn={crmOn} onFacturer={facturer} />}
      {showAdd && <AddResourceModal onClose={() => setShowAdd(false)} showNotification={showNotification} onCreated={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

// ── Drawer fiche ressource : Synthèse + CRA (lecture, circuit de validation) ─
function ResourceDrawer({ r, tab, setTab, onClose, onExpand, craNow, onSetStatus, onSignature, showNotification, onSaved, crmOn, onFacturer }) {
  const [month, setMonth] = useState(() => mKey(new Date()));
  const [cra, setCra] = useState(null);
  const [missionWiz, setMissionWiz] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCra(null);
    (async () => {
      try {
        const rr = await fetch(`${BASE}/resources/${r.id}/cra?month=${month}`, { headers: authH() });
        if (!rr.ok) { if (!cancelled) setCra({ days: {}, status: null }); return; }
        const j = await rr.json().catch(() => ({}));
        const d = j.data || j;
        if (!cancelled) setCra({ days: d.days || {}, status: d.status || 'draft', locked: !!d.locked, total: d.total });
      } catch { if (!cancelled) setCra({ days: {}, status: null }); }
    })();
    return () => { cancelled = true; };
  }, [r.id, month]);

  const pads = firstDayPad(month);
  const nDays = monthDays(month);
  const days = cra?.days || {};
  const total = cra?.total ?? Object.values(days).reduce((s, v) => s + (Number(v) || 0), 0);
  const stc = craChip(cra?.status);

  // Gestes du circuit — sur le mois affiché ; l'état local suit le succès.
  const changerStatut = async (status) => {
    if (await onSetStatus?.(status, month)) setCra(prev => prev ? { ...prev, status, locked: status !== 'draft' } : prev);
  };
  const demanderSignature = async () => {
    if (await onSignature?.(month)) setCra(prev => prev ? { ...prev, status: 'signature_en_cours' } : prev);
  };
  // Contrat serveur : repasser en draft ANNULE la demande de signature en cours.
  const annulerSignature = async () => {
    const ok = await confirmErpia({
      title: 'Annuler la demande de signature',
      message: `Annuler la signature du CRA ${mLabel(month)} de ${nameOf(r)} ?`,
      detail: 'Le lien de signature envoyé au client sera invalidé et le mois repassera en saisie.',
      confirmLabel: 'Annuler la demande', cancelLabel: 'Garder la demande', tone: 'danger',
    });
    if (ok) await changerStatut('draft');
  };
  const validerCompta = async () => {
    const ok = await confirmErpia({
      title: 'Valider pour la compta',
      message: `Valider définitivement le CRA ${mLabel(month)} de ${nameOf(r)} ?`,
      detail: 'Le mois est figé : c’est cette version qui sert à la facturation et qui alimentera la paie (Billy). Un déverrouillage reste possible tant que rien n’est facturé.',
      confirmLabel: 'Valider pour la compta', cancelLabel: 'Annuler', tone: 'primary',
    });
    if (ok) await changerStatut('valide_interne');
  };

  const INFO = [
    ['Mission', r.mission_titre], ['Client', r.client],
    ['Début', fmtDate(r.date_debut)], ['Fin', fmtDate(r.date_fin) || '—'],
    ['Taux d\'activité', r.taux_activite != null ? `${r.taux_activite}%` : '—'], ['Statut', r.statut || '—'],
    ['Compte lié', r.user_username || 'aucun'],
  ];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(8,9,12,0.45)' }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 61, width: 'min(520px,94vw)', background: 'var(--panel)', borderLeft: '1px solid var(--border)', boxShadow: '-18px 0 50px rgba(8,9,12,0.3)', overflowY: 'auto' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '11px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '14px', fontWeight: 600, flex: 'none' }}>{initialsOf(r)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{nameOf(r)}</div>
            <div style={{ marginTop: '1px', fontSize: '12.5px', color: 'var(--text-3)' }}>{[r.mission_titre, r.client].filter(Boolean).join(' · ')}</div>
          </div>
          <button onClick={() => onExpand?.(r)} className="src-row-hover" title="Ouvrir la vue détaillée"
            style={{ ...GHOST_BTN, width: '32px', height: '32px', padding: 0, justifyContent: 'center' }}><BI icon="arrows-angle-expand" style={{ fontSize: '12px' }} /></button>
          <button onClick={onClose} className="src-row-hover" style={{ ...GHOST_BTN, width: '32px', height: '32px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" style={{ fontSize: '13px' }} /></button>
        </div>

        <div style={{ display: 'flex', gap: '5px', padding: '10px 20px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)', overflowX: 'auto' }}>
          {[['synthese', 'Synthèse'], ['cra', 'CRA'], ['formation', 'Formation'], ['admin', 'Administratif'], ['actions', 'Actions'], ['acces', 'Accès']].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} style={SEG(tab === t)}>{l}</button>
          ))}
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {tab === 'synthese' && (
            <>
              <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ ...LBL, flex: 1 }}>Mission</span>
                  {crmOn && (
                    <button onClick={() => setMissionWiz(true)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '12px' }}>
                      <BI icon="plus-lg" style={{ fontSize: '10px' }} /> Créer une mission
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                  {INFO.map(([k, v]) => (
                    <div key={k}><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{k}</div><div style={{ marginTop: '2px', fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{v || '—'}</div></div>
                  ))}
                </div>
              </div>
              <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ ...LBL, flex: 1 }}>CRA du mois courant</span>
                  {craNow?.total != null && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-2)' }}>{craNow.total} j</span>}
                  <Chip kind={craChip(craNow?.status).k}>{craChip(craNow?.status).l}</Chip>
                </div>
              </div>
            </>
          )}

          {tab === 'cra' && (
            <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => setMonth(shiftMonth(month, -1))} className="src-row-hover" style={{ ...GHOST_BTN, width: '28px', height: '28px', padding: 0, justifyContent: 'center' }}><BI icon="chevron-left" style={{ fontSize: '11px' }} /></button>
                <span style={{ flex: 1, textAlign: 'center', fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)', textTransform: 'capitalize' }}>{mLabel(month)}</span>
                <button onClick={() => setMonth(shiftMonth(month, 1))} className="src-row-hover" style={{ ...GHOST_BTN, width: '28px', height: '28px', padding: 0, justifyContent: 'center' }}><BI icon="chevron-right" style={{ fontSize: '11px' }} /></button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <Chip kind={stc.k}>{stc.l}</Chip>
                {cra?.locked && <Chip kind="mut"><BI icon="lock-fill" style={{ fontSize: '9px' }} /> verrouillé</Chip>}
                <div style={{ flex: 1 }} />
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{total} j</span>
              </div>
              {cra === null ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '12.5px' }}><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement…</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', marginBottom: '4px' }}>
                    {['LU', 'MA', 'ME', 'JE', 'VE', 'SA', 'DI'].map(w => <span key={w} style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '8.5px', fontWeight: 600, color: 'var(--text-faint)' }}>{w}</span>)}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px' }}>
                    {Array.from({ length: pads }, (_, i) => <div key={`p${i}`} />)}
                    {Array.from({ length: nDays }, (_, i) => {
                      const d = i + 1; const we = isWeekend(month, d);
                      const v = Number(days[isoOf(month, d)] ?? 0);
                      return (
                        <div key={d} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', aspectRatio: '1/0.85', borderRadius: '6px',
                          background: we ? 'transparent' : v === 1 ? 'var(--accent)' : v === 0.5 ? 'var(--accent-soft-bg)' : 'var(--code-bg)',
                          color: we ? 'var(--text-faint)' : v === 1 ? 'var(--accent-fg)' : v === 0.5 ? 'var(--text-strong)' : 'var(--text-3)',
                          border: we ? '1px dashed var(--hair)' : '1px solid var(--hair)', opacity: we ? 0.55 : 1 }}>
                          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '8px', opacity: 0.65 }}>{d}</span>
                          <span style={{ fontSize: '10px', fontWeight: 700 }}>{v === 1 ? '1' : v === 0.5 ? '½' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Circuit de validation, sur le MOIS AFFICHÉ (le CRA d'août
                      se valide début septembre) : chaque état ne montre que
                      les gestes possibles depuis là. */}
                  {cra?.status === 'submitted' && (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={() => changerStatut('client_validated')} style={{ ...PRIMARY_BTN, padding: '8px 13px', fontSize: '12.5px' }}><BI icon="check-lg" /> Valider (client) — verrouille</button>
                      <button onClick={demanderSignature} className="src-row-hover" style={GHOST_BTN}><BI icon="pen" /> Faire signer par le client (signd)</button>
                    </div>
                  )}
                  {cra?.status === 'signature_en_cours' && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '12px', lineHeight: 1.5, color: 'var(--text-2)' }}>
                        <BI icon="pen" style={{ marginRight: '6px' }} />Le client a reçu ce CRA à signer (signd) — l'état passera en « Validé client » dès la signature.
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={() => changerStatut('client_validated')} className="src-row-hover" style={GHOST_BTN} title="Le client a validé autrement (email, papier) : valider sans attendre la signature électronique.">
                          <BI icon="check-lg" /> Valider sans attendre la signature
                        </button>
                        <button onClick={annulerSignature} className="src-row-hover" style={GHOST_BTN} title="Repasse le mois en saisie et annule la demande de signature chez le client.">
                          <BI icon="x-lg" /> Annuler la demande de signature
                        </button>
                      </div>
                    </div>
                  )}
                  {cra?.status === 'client_validated' && (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={validerCompta} style={{ ...PRIMARY_BTN, padding: '8px 13px', fontSize: '12.5px' }} title="Fige le mois pour la facturation et, demain, la paie (Billy).">
                        <BI icon="check2-all" /> Valider pour la compta
                      </button>
                      <button onClick={() => changerStatut('draft')} className="src-row-hover" style={GHOST_BTN}><BI icon="unlock" /> Déverrouiller</button>
                    </div>
                  )}
                  {cra?.status === 'valide_interne' && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '12px', lineHeight: 1.5, color: 'var(--text-2)' }}>
                        <BI icon="check2-all" style={{ marginRight: '6px' }} />Mois validé pour la compta — c'est cette version qui alimente la facturation (et, demain, les fiches de paie via Billy).
                      </div>
                      <div>
                        <button onClick={() => changerStatut('draft')} className="src-row-hover" style={GHOST_BTN}><BI icon="unlock" /> Déverrouiller (repasser en saisie)</button>
                      </div>
                    </div>
                  )}
                  {/* Facturation : dès la validation client, conseillée après la
                      validation compta — sinon bouton désactivé qui dit
                      pourquoi (jamais un clic mort). */}
                  {crmOn && cra && (
                    <div style={{ marginTop: '10px' }}>
                      {cra?.status === 'valide_interne'
                        ? <button onClick={() => onFacturer?.(r.id, month)} style={{ ...PRIMARY_BTN, padding: '8px 13px', fontSize: '12.5px' }}><BI icon="receipt" /> Générer la facture</button>
                        : cra?.status === 'client_validated'
                          ? <button onClick={() => onFacturer?.(r.id, month)} className="src-row-hover" style={GHOST_BTN} title="Conseillé : valider pour la compta d'abord."><BI icon="receipt" /> Générer la facture</button>
                          : <button disabled title="CRA non validé" style={{ ...GHOST_BTN, opacity: 0.5, cursor: 'not-allowed' }}><BI icon="receipt" /> Générer la facture</button>}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'formation' && <FormationSection rid={r.id} showNotification={showNotification} />}
          {tab === 'admin' && <AdminSection r={r} showNotification={showNotification} onSaved={onSaved} />}
          {tab === 'actions' && <ActionsSection rid={r.id} name={nameOf(r)} showNotification={showNotification} />}
          {tab === 'acces' && <AccessSection r={r} showNotification={showNotification} onSaved={onSaved} />}
        </div>
      </div>
      {missionWiz && <MissionAssistant r={r} onClose={() => setMissionWiz(false)} showNotification={showNotification}
        onDone={() => { setMissionWiz(false); onSaved?.(); }} />}
    </>
  );
}

// ── Sections de la fiche ressource (drawer + vue détaillée) ──────────────────
// Chaque collection vit sur GET/POST /resources/:id/{frais|documents|trainings|
// actions} — routes tolérées absentes (404/501 → encart « bientôt côté
// serveur »), la fiche reste utilisable.
function useResColl(rid, path) {
  const [items, setItems] = useState(null); // null = chargement
  const [missing, setMissing] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/resources/${rid}/${path}`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setMissing(true); setItems([]); return; }
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j[path] || (Array.isArray(j) ? j : []);
      setItems(Array.isArray(arr) ? arr : []);
    } catch { setItems([]); }
  }, [rid, path]);
  useEffect(() => { setItems(null); setMissing(false); load(); }, [load]);
  return { items, missing, reload: load };
}
const SectCard = ({ title, right, children }) => (
  <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <span style={{ ...LBL, flex: 1 }}>{title}</span>{right}
    </div>
    {children}
  </div>
);
const MissingNote = ({ children }) => (
  <div style={{ fontSize: '12px', color: 'var(--text-faint)', padding: '10px 12px', borderRadius: '8px', background: 'var(--code-bg)' }}>
    <BI icon="hourglass-split" style={{ marginRight: '6px' }} />{children}
  </div>
);
const chf = (n) => `${Number(n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`;

// Le statut d'une note de frais, en mots (le serveur envoie la clé).
const STATUTS_FRAIS = { pending: 'En attente', approved: 'Validée', rejected: 'Refusée', rembourse: 'Remboursée' };

// Notes de frais par mois : total mensuel + détail (vue manager, lecture).
function FraisSection({ rid }) {
  const { items, missing } = useResColl(rid, 'frais');
  const byMonth = useMemo(() => {
    const m = new Map();
    for (const f of items || []) {
      const d = f.date || f.created_at || '';
      const k = String(d).slice(0, 7) || 'Sans date';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);
  return (
    <SectCard title="Notes de frais">
      {items === null ? <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div>
        : missing ? <MissingNote>Les notes de frais ne sont pas encore disponibles sur ce serveur.</MissingNote>
          : byMonth.length === 0 ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune note de frais.</div>
            : byMonth.map(([mk, rows]) => (
              <div key={mk} style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid var(--hair)' }}>
                  <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)', textTransform: 'capitalize' }}>{/^\d{4}-\d{2}$/.test(mk) ? mLabel(mk) : mk}</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-strong)' }}>{chf(rows.reduce((s, f) => s + (Number(f.montant) || 0), 0))}</span>
                </div>
                {rows.map((f, i) => (
                  <div key={f.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', fontSize: '12.5px' }}>
                    <span style={{ flex: 1, color: 'var(--text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label || f.libelle || '—'}</span>
                    {(f.statut || f.status) && <Chip kind={/valid|approv|rembours/i.test(f.statut || f.status) ? 'ok' : /reje|refus/i.test(f.statut || f.status) ? 'bad' : 'mut'}>{STATUTS_FRAIS[f.statut || f.status] || f.statut || f.status}</Chip>}
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-2)', flex: 'none' }}>{chf(f.montant)}</span>
                  </div>
                ))}
              </div>
            ))}
    </SectCard>
  );
}

/**
 * Le formulaire de la ressource — envoyé, attendu, reçu.
 *
 * Il manquait le bout du chaînage : on composait un formulaire, on l'envoyait,
 * la personne le remplissait… et on ne pouvait relire ses réponses QUE depuis
 * l'assistant de placement. Une ressource déjà en poste, à qui on envoie le
 * formulaire depuis Forms ou depuis sa fiche, n'avait aucun écran où voir ce
 * qu'elle avait répondu.
 *
 * CE QUE CE BLOC DIT, ET QUI NE SE DEVINE PAS AILLEURS : quelles réponses ont
 * ATTERRI sur la fiche (celles qui ont une destination) et lesquelles ne
 * vivent que dans le PDF archivé. Un champ sans destination n'est pas perdu —
 * mais il ne remplira jamais la fiche, et c'est une chose à savoir avant de
 * s'étonner qu'une information « donnée » soit introuvable.
 *
 * Routes : GET /resources/:id/formulaire → { statut, envoye_le, recu_le,
 *          donnees, champs } ; POST /resources/:id/formulaire → (r)envoi.
 */
const ETAT_FORM = {
  recu: ['Reçu', 'ok'],
  envoye: ['En attente de réponse', 'warn'],
  non_envoye: ['Jamais envoyé', 'mut'],
};
function FormulaireSection({ r, showNotification, onSaved }) {
  const [etat, setEtat] = useState(undefined);   // undefined = chargement | null = route absente
  const [busy, setBusy] = useState(false);
  const [lien, setLien] = useState(null);        // { url, email_envoye, motif }
  const [copie, setCopie] = useState(false);

  const charger = useCallback(async () => {
    try {
      const rr = await fetch(`${BASE}/resources/${r.id}/formulaire`, { headers: authH() });
      if ([404, 405, 501].includes(rr.status)) { setEtat(null); return; }
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) { setEtat(null); return; }
      setEtat(j.data || j);
    } catch { setEtat(null); }
  }, [r.id]);
  useEffect(() => { charger(); }, [charger]);

  const envoyer = async () => {
    const qui = [r.prenom, r.nom].filter(Boolean).join(' ') || 'cette personne';
    const deja = etat?.statut && etat.statut !== 'non_envoye';
    if (deja) {
      const ok = await confirmErpia({
        title: 'Renvoyer le formulaire',
        message: `${qui} a déjà ${etat.statut === 'recu' ? 'répondu' : 'reçu un lien'}.`,
        detail: etat.statut === 'recu'
          ? 'Un nouvel envoi lui redemande tout et remplacera ses réponses actuelles.'
          : "Le lien précédent cessera de fonctionner — s'il l'avait commencé, son brouillon devient inaccessible.",
        confirmLabel: 'Renvoyer quand même',
      });
      if (!ok) return;
    }
    setBusy(true); setLien(null);
    try {
      const rr = await fetch(`${BASE}/resources/${r.id}/formulaire`, { method: 'POST', headers: jsonH(), body: '{}' });
      const j = await rr.json().catch(() => ({}));
      if ([404, 405, 501].includes(rr.status) && !j.error) { showNotification?.("L'envoi du formulaire arrive côté serveur — rien n'est parti.", 'info'); return; }
      if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
      setLien({ url: j.url || j.lien || '', email_envoye: j.email_envoye, motif: j.email_erreur });
      setCopie(false);
      // L'email qui n'est pas parti ne s'annonce pas comme envoyé : sinon on
      // attend une réponse à un message que personne n'a reçu.
      if (j.email_envoye === false) showNotification?.(j.email_erreur || "Le lien est prêt mais l'email n'est pas parti — transmettez-le vous-même.", 'error');
      else showNotification?.(j.message || 'Formulaire envoyé.', 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (etat === null) return null;   // route absente : on n'annonce pas un bloc vide
  const [label, kind] = ETAT_FORM[etat?.statut] || ETAT_FORM.non_envoye;
  const champs = Array.isArray(etat?.champs) ? etat.champs : [];
  const donnees = etat?.donnees && typeof etat.donnees === 'object' ? etat.donnees : {};
  const repondus = champs.filter(c => String(donnees[c.cle] ?? '').trim() !== '');
  const surLaFiche = repondus.filter(c => c.destination);
  const librement = repondus.filter(c => !c.destination);
  const jour = (v) => { const d = v ? new Date(v) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' }) : null; };

  const ligne = (c) => (
    <div key={c.cle} style={{ display: 'flex', alignItems: 'baseline', gap: '9px', padding: '5px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px' }}>
      <span style={{ flex: '0 0 44%', color: 'var(--text-3)', minWidth: 0 }}>{c.libelle}</span>
      <span style={{ flex: 1, color: 'var(--text-strong)', minWidth: 0, wordBreak: 'break-word', ...(/avs|iban|npa/i.test(c.cle) ? MONO : null) }}>{String(donnees[c.cle])}</span>
    </div>
  );

  return (
    <SectCard title="Formulaire"
      right={<button onClick={envoyer} disabled={busy} style={{ ...GHOST_BTN, opacity: busy ? 0.6 : 1 }}>
        <BI icon={busy ? 'arrow-repeat' : 'send'} />{busy ? 'Envoi…' : etat?.statut === 'non_envoye' ? 'Envoyer le formulaire' : 'Renvoyer'}
      </button>}>
      {etat === undefined ? (
        <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', marginBottom: repondus.length ? '10px' : 0 }}>
            <Chip kind={kind}>{label}</Chip>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>
              {etat.statut === 'recu' ? `Réponse reçue le ${jour(etat.recu_le) || '—'}`
                : etat.statut === 'envoye' ? `Envoyé le ${jour(etat.envoye_le) || '—'} — sans réponse pour l'instant`
                  : "Ses informations administratives n'ont jamais été demandées."}
            </span>
            {champs.length > 0 && <span style={{ ...MONO, fontSize: '11px', color: 'var(--text-faint)' }}>{repondus.length}/{champs.length}</span>}
          </div>

          {lien && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', padding: '9px 11px', marginBottom: '10px', borderRadius: '9px', border: '1px solid var(--accent)', background: 'var(--accent-soft-bg)' }}>
              <span style={{ flex: 1, minWidth: '180px', fontSize: '11.5px' }}>
                <strong>{lien.email_envoye === false ? "L'email n'est pas parti — transmettez ce lien" : 'Lien envoyé'}</strong>
                <code style={{ ...MONO, display: 'block', marginTop: '3px', fontSize: '11px', wordBreak: 'break-all', color: 'var(--text-2)' }}>{lien.url || '—'}</code>
              </span>
              {lien.url && <button style={GHOST_BTN} onClick={async () => { try { await navigator.clipboard.writeText(lien.url); setCopie(true); } catch { showNotification?.('Copie impossible — sélectionnez le lien à la main.', 'error'); } }}>{copie ? 'Copié' : 'Copier'}</button>}
            </div>
          )}

          {surLaFiche.length > 0 && (
            <>
              <div style={{ ...LBL, marginBottom: '4px' }}>Repris sur la fiche</div>
              {surLaFiche.map(ligne)}
            </>
          )}
          {/* Une réponse sans destination n'est pas perdue — mais elle ne
              remplira jamais la fiche. Le dire ici évite de la chercher. */}
          {librement.length > 0 && (
            <>
              <div style={{ ...LBL, marginTop: surLaFiche.length ? '12px' : 0, marginBottom: '4px' }}>Dans le PDF uniquement</div>
              {librement.map(ligne)}
            </>
          )}
          {etat.statut === 'recu' && repondus.length === 0 && (
            <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Le formulaire est revenu vide.</div>
          )}
          {etat.statut === 'recu' && (
            <div style={{ marginTop: '10px', fontSize: '11.5px', color: 'var(--text-3)' }}>
              Le PDF rempli est classé dans les documents ci-dessous. Les réponses ne remplacent jamais une valeur déjà saisie sur la fiche —
              {' '}<button type="button" onClick={onSaved} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--text-2)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>recharger la fiche</button>.
            </div>
          )}
        </>
      )}
    </SectCard>
  );
}

// Administratif : permis (alerte d'expiration), AVS — PUT /resources/:id ;
// documents de la ressource en dessous.
const expInfo = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (Number.isNaN(d)) return null;
  if (d < 0) return { k: 'bad', l: 'expiré' };
  if (d <= 90) return { k: 'warn', l: `expire dans ${d} j` };
  return { k: 'ok', l: 'valide' };
};
// LES CHAMPS QUE LE FORMULAIRE REMPLIT. Ils étaient écrits en base et renvoyés
// par l'API, mais la fiche n'en affichait que deux (permis, AVS) : on demandait
// son adresse et son IBAN à quelqu'un, il répondait, et on ne pouvait plus les
// relire nulle part. Ils se corrigent aussi ici — le formulaire ne remplit QUE
// les cases vides (COALESCE côté serveur), donc ce qui est saisi ici fait foi.
const CHAMPS_ADMIN = ['permis', 'permis_expiration', 'avs', 'adresse', 'npa', 'ville', 'date_naissance', 'nationalite', 'etat_civil', 'iban'];
// Un <input type="date"> refuse un horodatage : il lui faut AAAA-MM-JJ nu.
const valeurAdmin = (r, k) => (/date|expiration/.test(k) ? String(r[k] || '').slice(0, 10) : (r[k] || ''));
const saisieAdmin = (r) => ({
  ...Object.fromEntries(CHAMPS_ADMIN.map(k => [k, valeurAdmin(r, k)])),
  conges_acquis: r.conges_acquis_par_an != null ? String(r.conges_acquis_par_an) : '',
});

function AdminSection({ r, showNotification, onSaved }) {
  const [f, setF] = useState(() => saisieAdmin(r));
  const [saving, setSaving] = useState(false);
  const { items: docs, missing: docsMissing } = useResColl(r.id, 'documents');
  // La fiche se recharge après une réponse au formulaire : sans ça, les
  // valeurs qui viennent d'atterrir resteraient invisibles jusqu'au F5.
  useEffect(() => { setF(saisieAdmin(r)); }, [r]);
  const dirty = CHAMPS_ADMIN.some(k => f[k] !== valeurAdmin(r, k))
    || f.conges_acquis !== (r.conges_acquis_par_an != null ? String(r.conges_acquis_par_an) : '');
  const exp = expInfo(f.permis_expiration);
  const save = async () => {
    // Quota de congés : nombre (demi-jours permis) ou vide = pas de quota —
    // le solde disparaît alors de l'espace de la ressource.
    const quotaBrut = String(f.conges_acquis).trim().replace(',', '.');
    const quota = quotaBrut === '' ? null : Number(quotaBrut);
    if (quota != null && (!Number.isFinite(quota) || quota < 0)) { showNotification?.('Congés acquis / an : indiquez un nombre de jours (ex. 25 ou 12,5), ou laissez vide pour retirer le quota.', 'error'); return; }
    setSaving(true);
    try {
      const rr = await fetch(`${BASE}/resources/${r.id}`, {
        method: 'PUT', headers: jsonH(),
        body: JSON.stringify({ ...Object.fromEntries(CHAMPS_ADMIN.map(k => [k, f[k] || null])), conges_acquis_par_an: quota }),
      });
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
      showNotification?.('Infos administratives enregistrées.', 'success');
      onSaved?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setSaving(false); }
  };
  const fld = (label, k, props = {}, extra = null) => (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '7px' }}>{label}{extra}</div>
      <input value={f[k]} onChange={e => setF(p => ({ ...p, [k]: e.target.value }))} style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} {...props} />
    </div>
  );
  return (
    <>
      <SectCard title="Infos administratives"
        right={dirty && <button onClick={save} disabled={saving} style={{ ...PRIMARY_BTN, padding: '6px 11px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}><BI icon={saving ? 'arrow-repeat' : 'check-lg'} /> Enregistrer</button>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
          {fld('Permis de travail', 'permis', { placeholder: 'B / C / G / CH…' })}
          {fld('Expiration du permis', 'permis_expiration', { type: 'date' }, exp && <Chip kind={exp.k}>{exp.l}</Chip>)}
          {fld('N° AVS', 'avs', { placeholder: '756.xxxx.xxxx.xx' })}
          {fld('Congés acquis / an (jours)', 'conges_acquis', { type: 'number', min: 0, step: 0.5, placeholder: 'ex. 25 — vide : pas de quota' })}
          {fld('Date de naissance', 'date_naissance', { type: 'date' })}
          {fld('Nationalité', 'nationalite', { placeholder: 'Suisse' })}
          {fld('État civil', 'etat_civil', { placeholder: 'Célibataire, marié·e…' })}
          {fld('NPA', 'npa', { placeholder: '1204' })}
          {fld('Ville', 'ville', { placeholder: 'Genève' })}
          {fld('IBAN de versement', 'iban', { placeholder: 'CH00 0000 0000 0000 0000 0' })}
        </div>
        <div style={{ marginTop: '10px' }}>{fld('Adresse', 'adresse', { placeholder: 'Rue et numéro' })}</div>
      </SectCard>
      <FormulaireSection r={r} showNotification={showNotification} onSaved={onSaved} />
      <SectCard title="Documents">
        {docs === null ? <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div>
          : docsMissing ? <MissingNote>Les documents ne sont pas encore disponibles sur ce serveur.</MissingNote>
            : docs.length === 0 ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun document — la checklist (contrat signé, permis, AVS, IBAN) se remplit depuis la conversion ou l'espace ressource.</div>
              : docs.map((d, i) => (
                <div key={d.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '6px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px' }}>
                  <BI icon="file-earmark" style={{ color: 'var(--text-3)', flex: 'none' }} />
                  <span style={{ flex: 1, color: 'var(--text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label || d.name || d.file_name || 'Document'}</span>
                  {d.url && <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-2)', flex: 'none' }} title="Télécharger"><BI icon="download" /></a>}
                </div>
              ))}
      </SectCard>
    </>
  );
}

// Formation : historique + déclaration/planification ; connecteur temptraining
// (app Marketplace) — planifiée + module actif = financement demandé auto.
function FormationSection({ rid, showNotification }) {
  const temptrainingOn = useModuleApp('temptraining');
  const { items, missing, reload } = useResColl(rid, 'trainings');
  const [form, setForm] = useState(null); // { titre, organisme, date, statut }
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!form?.titre?.trim()) { showNotification?.('Le titre de la formation est requis.', 'error'); return; }
    setSaving(true);
    try {
      const rr = await fetch(`${BASE}/resources/${rid}/trainings`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ titre: form.titre.trim(), organisme: form.organisme?.trim() || undefined, date: form.date || undefined, statut: form.statut || 'planifiee' }),
      });
      if (rr.status === 404 || rr.status === 501) { showNotification?.('La déclaration de formation arrive côté serveur.', 'error'); return; }
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
      showNotification?.(form.statut === 'realisee' ? 'Formation enregistrée.'
        : temptrainingOn ? 'Formation planifiée — demande de financement temptraining envoyée automatiquement.' : 'Formation planifiée.', 'success');
      setForm(null); reload();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setSaving(false); }
  };
  return (
    <SectCard title="Formation"
      right={!form && <button onClick={() => setForm({ titre: '', organisme: '', date: '', statut: 'planifiee' })} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon="plus-lg" style={{ fontSize: '10px' }} /> Déclarer</button>}>
      {!temptrainingOn && (
        <div style={{ marginBottom: '10px', fontSize: '12px', color: 'var(--text-2)', padding: '9px 12px', borderRadius: '8px', background: 'var(--code-bg)' }}>
          <BI icon="mortarboard" style={{ marginRight: '6px' }} />Activez l'app <b>temptraining</b> dans le Marketplace : chaque formation planifiée part automatiquement en demande de financement.
        </div>
      )}
      {form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px', padding: '11px 12px', borderRadius: '9px', border: '1px solid var(--hair)' }}>
          <input value={form.titre} onChange={e => setForm(p => ({ ...p, titre: e.target.value }))} placeholder="Titre de la formation *" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} autoFocus />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: '8px' }}>
            <input value={form.organisme} onChange={e => setForm(p => ({ ...p, organisme: e.target.value }))} placeholder="Organisme" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} />
            <input value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} type="date" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Seg options={['planifiee', 'realisee']} value={form.statut} onChange={v => setForm(p => ({ ...p, statut: v }))} />
            <div style={{ flex: 1 }} />
            <button onClick={() => setForm(null)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}>Annuler</button>
            <button onClick={add} disabled={saving} style={{ ...PRIMARY_BTN, padding: '6px 12px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}><BI icon="check-lg" /> Ajouter</button>
          </div>
        </div>
      )}
      {items === null ? <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div>
        : missing ? <MissingNote>L'historique de formation n'est pas encore disponible.</MissingNote>
          : items.length === 0 ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune formation déclarée.</div>
            : items.map((t, i) => (
              <div key={t.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px', flexWrap: 'wrap' }}>
                <BI icon="mortarboard" style={{ color: 'var(--text-3)', flex: 'none' }} />
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{t.titre || t.title || '—'}</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>{[t.organisme, fmtDate(t.date)].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                <Chip kind={/realis/i.test(t.statut || '') ? 'ok' : 'warn'}>{/realis/i.test(t.statut || '') ? 'réalisée' : 'planifiée'}</Chip>
                {t.financement && <Chip kind={/accord|valid/i.test(t.financement) ? 'ok' : 'mut'} title="Financement temptraining">💰 {t.financement}</Chip>}
              </div>
            ))}
    </SectCard>
  );
}

// Actions typées (Note / Rappel / Suivi / Entretien RH / Appel).
const ACTION_TYPES = [
  ['note', 'Note', 'journal-text'],
  ['rappel', 'Rappel', 'bell'],
  ['suivi', 'Suivi', 'graph-up'],
  ['entretien', 'Entretien RH', 'chat-dots'],
  ['appel', 'Appel', 'telephone'],
];
function ActionsSection({ rid, name, showNotification }) {
  const { items, missing, reload } = useResColl(rid, 'actions');
  const team = useTeam();
  const [form, setForm] = useState(null); // { type, texte, date }
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!form?.texte?.trim()) { showNotification?.('Le texte est requis.', 'error'); return; }
    setSaving(true);
    try {
      const rr = await fetch(`${BASE}/resources/${rid}/actions`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ type: form.type, texte: form.texte.trim(), date: form.date || undefined }),
      });
      if (rr.status === 404 || rr.status === 501) { showNotification?.('Les actions arrivent côté serveur.', 'error'); return; }
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
      // @mentions : les collègues tagués dans le texte reçoivent une
      // notification in-app (clic → écran Ressources)
      const me = (() => { try { return APIService.getCurrentUser(); } catch { return null; } })();
      const typeLabel = (ACTION_TYPES.find(([k]) => k === form.type)?.[1] || 'action').toLowerCase();
      const n = await notifyMentions({
        text: form.texte, team,
        message: `📌 ${me?.username || 'Un collègue'} vous a mentionné dans un(e) ${typeLabel} sur la ressource ${name || ''}`.trim(),
        refType: 'ressource', refId: rid,
      });
      showNotification?.(n ? `Action enregistrée — ${n} personne(s) notifiée(s).` : 'Action enregistrée.', 'success');
      setForm(null); reload();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setSaving(false); }
  };
  const typeOf = (t) => ACTION_TYPES.find(([k]) => k === (t.type || 'note')) || ACTION_TYPES[0];
  return (
    <SectCard title="Actions & rappels"
      right={!form && <button onClick={() => setForm({ type: 'note', texte: '', date: '' })} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon="plus-lg" style={{ fontSize: '10px' }} /> Ajouter</button>}>
      {form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px', padding: '11px 12px', borderRadius: '9px', border: '1px solid var(--hair)' }}>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {ACTION_TYPES.map(([k, l, ic]) => (
              <button key={k} onClick={() => setForm(p => ({ ...p, type: k }))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, background: form.type === k ? 'var(--accent)' : 'var(--code-bg)', color: form.type === k ? 'var(--accent-fg)' : 'var(--text-2)' }}>
                <BI icon={ic} style={{ fontSize: '10px' }} /> {l}
              </button>
            ))}
          </div>
          <MentionArea value={form.texte} onChange={v => setForm(p => ({ ...p, texte: v }))} team={team} rows={2} autoFocus
            placeholder="Que s'est-il passé / que faut-il faire ? Tapez @ pour mentionner un collègue" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} type="date" style={{ ...INPUT, padding: '7px 10px', fontSize: '12.5px', width: '150px' }} title="Échéance (rappel)" />
            <div style={{ flex: 1 }} />
            <button onClick={() => setForm(null)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}>Annuler</button>
            <button onClick={add} disabled={saving} style={{ ...PRIMARY_BTN, padding: '6px 12px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}><BI icon="check-lg" /> Enregistrer</button>
          </div>
        </div>
      )}
      {items === null ? <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div>
        : missing ? <MissingNote>Le suivi des actions n'est pas encore disponible.</MissingNote>
          : items.length === 0 ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune action ni rappel.</div>
            : items.map((a, i) => {
              const [, l, ic] = typeOf(a);
              return (
                <div key={a.id ?? i} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', padding: '7px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px' }}>
                  <BI icon={ic} style={{ color: 'var(--text-3)', flex: 'none', marginTop: '2px' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--text)' }}>{renderMentions(a.texte || a.text || a.label || '—')}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '1px' }}>{[l, fmtDate(a.date || a.created_at), a.author || a.username].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              );
            })}
    </SectCard>
  );
}

// ── Accès : activation du compte de la ressource (saisie CRA / Mon espace) ───
// Lier un compte = PUT /resources/:id { user_id } (le back pose le rôle
// « ressource ») ; créer = POST /config/users (renvoie un temp_password à
// transmettre UNE fois) puis liaison. Délier = user_id: null.
function AccessSection({ r, showNotification, onSaved }) {
  const team = useTeam(); // annuaire org (id + username) — comptes existants liables
  const [mode, setMode] = useState(null);   // null | 'create' | 'link'
  const [form, setForm] = useState({ username: '', email: '' });
  const [sendMail, setSendMail] = useState(true); // envoi des identifiants par email (back)
  const [notifyMail, setNotifyMail] = useState(true); // liaison : prévenir la personne par email
  const [linkId, setLinkId] = useState('');
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null); // { username, password, mailed } — affiché UNE fois
  const [identityOpen, setIdentityOpen] = useState(false);
  const [identity, setIdentity] = useState({ username: r.user_username || '', email: r.user_email || r.email || '' });

  const suggestedUsername = `${(r.prenom || '').toLowerCase()}.${(r.nom || '').toLowerCase()}`.replace(/[^a-z0-9.]/g, '').replace(/^\.|\.$/g, '');

  const linkUser = async (userId) => {
    const rr = await fetch(`${BASE}/resources/${r.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ user_id: userId }) });
    const j = await rr.json().catch(() => ({}));
    if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
  };

  const createAndLink = async () => {
    const username = form.username.trim(), email = form.email.trim();
    if (!username || !email) { showNotification?.('Nom d\'utilisateur et email requis.', 'error'); return; }
    setBusy(true);
    try {
      const rr = await fetch(`${BASE}/config/users`, {
        method: 'POST', headers: jsonH(),
        // send_credentials : le back envoie les identifiants par email (Resend org)
        // et répond credentials_sent: true — s'il l'ignore, le panneau one-time
        // ci-dessous reste le seul canal (transmettre à la main).
        body: JSON.stringify({ username, email, prenom: r.prenom || '', nom: r.nom || '', admin: false, recruteuse: false, send_credentials: sendMail }),
      });
      const j = await rr.json().catch(() => ({}));
      if (rr.status === 409) { showNotification?.(j.error || j.message || 'Nom d\'utilisateur ou email déjà pris.', 'error'); return; }
      if (!rr.ok || j.success === false) throw new Error(j.error || `HTTP ${rr.status}`);
      // id du compte créé : dans la réponse, sinon retrouvé par username
      let uid = j.id ?? j.user_id ?? j.user?.id ?? j.data?.id ?? null;
      if (uid == null) {
        const lu = await fetch(`${BASE}/users`, { headers: authH() });
        const lj = await lu.json().catch(() => ({}));
        uid = ((lj.data || lj.users || (Array.isArray(lj) ? lj : [])).find(u => u.username === username) || {}).id ?? null;
      }
      if (uid == null) throw new Error('compte créé mais introuvable pour la liaison — liez-le manuellement');
      await linkUser(uid);
      const mailed = j.credentials_sent === true;
      if (j.temp_password) setCreds({ username, password: j.temp_password, mailed, email, sendError: j.send_error || null });
      showNotification?.(mailed
        ? `Compte ${username} créé et lié — identifiants envoyés par email à ${email}.`
        : `Compte ${username} créé et lié — ${nameOf(r)} peut saisir son CRA dans « Mon espace ».`, 'success');
      setMode(null); onSaved?.();
    } catch (e) { showNotification?.('Erreur activation du compte : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const linkExisting = async () => {
    if (!linkId) { showNotification?.('Choisissez un compte.', 'error'); return; }
    setBusy(true);
    try {
      await linkUser(Number(linkId));
      const linked = team.find(u => String(u.id) === String(linkId));
      // Liaison ≠ création : la personne garde ses identifiants, AUCUN email
      // n'est envoyé. On la prévient : notification in-app immédiate + email
      // via le back si la route existe (tolérée absente).
      if (linked) {
        fetch(`${BASE}/notifications/inapp`, {
          method: 'POST', headers: jsonH(),
          body: JSON.stringify({ user_id: linked.id, message: `Votre espace ressource est activé — retrouvez Timesheet, congés et notes de frais dans « Mon espace ».`, type: 'info', ref_type: 'ressource', ref_id: r.id }),
        }).catch(() => {});
        if (notifyMail) {
          // Contrat livré : 200 { success, data: { sent, email }, message } ;
          // 404 fiche/compte introuvable, 400 aucun email, 502 échec d'envoi
          // (error explicite) — on affiche les messages du back tels quels.
          try {
            const nr = await fetch(`${BASE}/resources/${r.id}/notify-access`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ user_id: linked.id }) });
            const nj = await nr.json().catch(() => ({}));
            if (nr.ok && nj.success !== false) {
              showNotification?.(`Compte lié. ${nj.message || `Email envoyé à ${nj.data?.email || linked.email || linked.username}.`}`, 'success');
            } else {
              const why = nj.error || nj.message || (nr.status === 400 ? 'aucun email sur ce compte' : `HTTP ${nr.status}`);
              showNotification?.(`Compte lié, mais email non envoyé : ${why} — prévenez la personne vous-même.`, 'error');
            }
          } catch { showNotification?.('Compte lié, mais email non envoyé (réseau) — prévenez la personne vous-même.', 'error'); }
        } else {
          showNotification?.('Compte lié — le rôle « ressource » est posé, la personne accède à « Mon espace » avec ses identifiants habituels.', 'success');
        }
      } else {
        showNotification?.('Compte lié — le rôle « ressource » est posé, la personne accède à « Mon espace ».', 'success');
      }
      setMode(null); onSaved?.();
    } catch (e) { showNotification?.(`Erreur liaison (PUT /resources/${r.id}) : ` + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!await confirmErpia({ message: `Délier le compte ${r.user_username} ? La personne perdra l'accès à « Mon espace ».` })) return;
    setBusy(true);
    try {
      await linkUser(null);
      showNotification?.('Compte délié.', 'success');
      onSaved?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const resendAccess = async () => {
    setBusy(true);
    try {
      const rr = await fetch(BASE + '/resources/' + r.id + '/notify-access', { method: 'POST', headers: jsonH(), body: JSON.stringify({ user_id: r.user_id }) });
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || j.message || ('HTTP ' + rr.status));
      showNotification?.(j.message || 'Email d’accès renvoyé.', 'success');
    } catch (e) { showNotification?.('Envoi impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const resetAccess = async () => {
    if (!await confirmErpia({ message: 'Réinitialiser les identifiants de ' + r.user_username + ' ? Un nouveau mot de passe provisoire sera créé.' })) return;
    setBusy(true);
    try {
      const rr = await fetch(BASE + '/config/users/' + r.user_id + '/reset-credentials', { method: 'POST', headers: jsonH(), body: JSON.stringify({ send_credentials: true }) });
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || ('HTTP ' + rr.status));
      setCreds({ username: j.user?.username || r.user_username, password: j.temp_password, mailed: j.credentials_sent === true, email: j.user?.email || r.user_email, sendError: j.send_error || null });
      showNotification?.(j.credentials_sent ? 'Identifiants réinitialisés et envoyés par email.' : 'Identifiants réinitialisés — transmettez le mot de passe provisoire affiché.', j.credentials_sent ? 'success' : 'error');
    } catch (e) { showNotification?.('Réinitialisation impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const saveIdentity = async () => {
    const username = identity.username.trim(), email = identity.email.trim();
    if (!username || !email) { showNotification?.('Identifiant et email requis.', 'error'); return; }
    setBusy(true);
    try {
      const rr = await fetch(BASE + '/config/users/' + r.user_id + '/identity', { method: 'PUT', headers: jsonH(), body: JSON.stringify({ username, email }) });
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || ('HTTP ' + rr.status));
      setIdentityOpen(false); showNotification?.('Identifiant de connexion mis à jour.', 'success'); onSaved?.();
    } catch (e) { showNotification?.('Modification impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const toggleAccess = async () => {
    const active = r.user_active === false;
    setBusy(true);
    try {
      const rr = await fetch(BASE + '/config/users/' + r.user_id + '/active', { method: 'PUT', headers: jsonH(), body: JSON.stringify({ active }) });
      const j = await rr.json().catch(() => ({}));
      if (!rr.ok || j.success === false) throw new Error(j.error || ('HTTP ' + rr.status));
      showNotification?.(active ? 'Accès réactivé.' : 'Accès désactivé.', 'success'); onSaved?.();
    } catch (e) { showNotification?.('Changement d’accès impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const copyCreds = async () => {
    const txt = `Identifiants erpia — ${nameOf(r)}\nUtilisateur : ${creds.username}\nMot de passe provisoire : ${creds.password}\n\nConnectez-vous puis saisissez votre CRA, congés et notes de frais dans « Mon espace ».`;
    try { await navigator.clipboard.writeText(txt); showNotification?.('Identifiants copiés — transmettez-les à la ressource.', 'success'); }
    catch { await alertErpia({ title: 'Copier les identifiants', message: 'Le presse-papiers est indisponible. Sélectionnez puis copiez les identifiants ci-dessous.', detail: `${creds.username} / ${creds.password}` }); }
  };

  return (
    <SectCard title="Compte & accès">
      {r.user_username ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '11px 14px', borderRadius: '10px', background: r.user_active === false ? 'var(--code-bg)' : 'var(--accent-soft-bg)', border: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <BI icon={r.user_active === false ? 'person-x-fill' : 'person-check-fill'} style={{ fontSize: '16px', color: r.user_active === false ? 'var(--text-3)' : 'var(--accent)', flex: 'none' }} />
            <div style={{ flex: 1, minWidth: '180px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>Compte {r.user_active === false ? 'désactivé' : 'actif'} — {r.user_username}</div>
              <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '2px' }}>{r.user_email || r.email || 'Email non renseigné'}</div>
            </div>
            <Chip kind={r.user_active === false ? 'mut' : 'ok'}>{r.user_active === false ? 'Accès coupé' : 'Accès actif'}</Chip>
          </div>
          {identityOpen ? (
            <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <input value={identity.username} onChange={e => setIdentity(p => ({ ...p, username: e.target.value }))} placeholder="Identifiant" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} />
              <input value={identity.email} onChange={e => setIdentity(p => ({ ...p, email: e.target.value }))} type="email" placeholder="Email" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} />
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => setIdentityOpen(false)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}>Annuler</button>
                <button onClick={saveIdentity} disabled={busy} style={{ ...PRIMARY_BTN, padding: '6px 12px', fontSize: '12px' }}><BI icon="check-lg" /> Enregistrer</button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={resendAccess} disabled={busy} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon="envelope-arrow-up" /> Renvoyer l’accès</button>
              <button onClick={resetAccess} disabled={busy} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon="key" /> Réinitialiser</button>
              <button onClick={() => { setIdentity({ username: r.user_username || '', email: r.user_email || r.email || '' }); setIdentityOpen(true); }} disabled={busy} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon="pencil" /> Modifier l’identifiant</button>
              <button onClick={toggleAccess} disabled={busy} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon={r.user_active === false ? 'person-check' : 'person-slash'} /> {r.user_active === false ? 'Réactiver' : 'Désactiver'}</button>
              <button onClick={unlink} disabled={busy} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}><BI icon="person-dash" /> Délier</button>
            </div>
          )}
          {creds && (
            <div style={{ marginTop: '10px', border: '1px solid var(--border)', background: 'var(--code-bg)', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Nouveaux identifiants — affichés une seule fois</div>
              <div style={{ marginTop: '6px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-2)' }}>{creds.username} · {creds.password}</div>
              <button onClick={copyCreds} style={{ ...PRIMARY_BTN, marginTop: '8px', padding: '6px 11px', fontSize: '12px' }}><BI icon="clipboard" /> Copier</button>
            </div>
          )}
        </>
      ) : creds ? (
        <div style={{ border: '1px solid rgba(217,119,6,0.45)', background: 'rgba(217,119,6,0.07)', borderRadius: '10px', padding: '13px 15px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '8px' }}><BI icon="key-fill" style={{ marginRight: '7px' }} />Identifiants à transmettre à {nameOf(r)}</div>
          {[['Utilisateur', creds.username], ['Mot de passe provisoire', creds.password]].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '3px 0', fontSize: '12.5px' }}>
              <span style={{ color: 'var(--text-3)', width: '160px', flex: 'none' }}>{k}</span>
              <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: 'var(--text-strong)' }}>{v}</code>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button onClick={copyCreds} style={{ ...PRIMARY_BTN, padding: '7px 12px', fontSize: '12px' }}><BI icon="clipboard" /> Copier les identifiants</button>
            <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#d97706' }}>Le mot de passe ne sera plus affiché.</span>
          </div>
          <div style={{ marginTop: '8px', fontSize: '11.5px', color: creds.mailed ? '#16a34a' : creds.sendError ? '#d97706' : 'var(--text-3)' }}>
            {creds.mailed
              ? <><BI icon="envelope-check" style={{ marginRight: '5px' }} />Identifiants aussi envoyés par email à {creds.email}.</>
              : creds.sendError
                ? <><BI icon="exclamation-triangle-fill" style={{ marginRight: '5px' }} />L'envoi par email a échoué : {creds.sendError} — transmettez les identifiants vous-même (bouton Copier).</>
                : <><BI icon="envelope-slash" style={{ marginRight: '5px' }} />Identifiants non envoyés par email — transmettez-les vous-même (bouton Copier).</>}
          </div>
        </div>
      ) : mode === 'create' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} placeholder="Nom d'utilisateur *" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} autoFocus />
            <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} type="email" placeholder="Email *" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={sendMail} onChange={e => setSendMail(e.target.checked)} style={{ accentColor: 'var(--accent)', width: '14px', height: '14px', cursor: 'pointer' }} />
            Envoyer les identifiants par email à la ressource
          </label>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setMode(null)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}>Annuler</button>
            <button onClick={createAndLink} disabled={busy} style={{ ...PRIMARY_BTN, padding: '6px 12px', fontSize: '12px', opacity: busy ? 0.6 : 1 }}>
              <BI icon={busy ? 'arrow-repeat' : 'person-plus'} /> Créer & activer
            </button>
          </div>
        </div>
      ) : mode === 'link' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <ErpiaSelect value={linkId} onChange={e => setLinkId(e.target.value)} style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px', flex: 1, minWidth: '180px' }}>
              <option value="">{team.length ? 'Choisir un compte existant…' : 'Chargement de l\'annuaire…'}</option>
              {team.map(u => <option key={u.id} value={u.id}>{u.username}{u.email ? ` — ${u.email}` : ''}</option>)}
            </ErpiaSelect>
            <button onClick={() => setMode(null)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '6px 11px', fontSize: '12px' }}>Annuler</button>
            <button onClick={linkExisting} disabled={busy || !linkId} style={{ ...PRIMARY_BTN, padding: '6px 12px', fontSize: '12px', opacity: busy || !linkId ? 0.5 : 1 }}>
              <BI icon={busy ? 'arrow-repeat' : 'link-45deg'} /> {busy ? 'Liaison…' : 'Lier'}
            </button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={notifyMail} onChange={e => setNotifyMail(e.target.checked)} style={{ accentColor: 'var(--accent)', width: '14px', height: '14px', cursor: 'pointer' }} />
            Prévenir la personne par email
          </label>
          <div style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
            La personne garde ses identifiants actuels — la liaison n'envoie pas de nouveaux accès. Son menu bascule sur « Mon espace » à sa prochaine connexion, et elle reçoit une notification dans l'app.
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.55, marginBottom: '10px' }}>
            {nameOf(r)} n'a pas encore d'accès — activez son compte pour qu'il puisse saisir son CRA, ses congés et ses notes de frais dans « Mon espace ».
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => { setForm({ username: suggestedUsername, email: '' }); setMode('create'); }} style={{ ...PRIMARY_BTN, padding: '7px 13px', fontSize: '12.5px' }}>
              <BI icon="person-plus" /> Créer & activer le compte
            </button>
            <button onClick={() => setMode('link')} className="src-row-hover" style={{ ...GHOST_BTN, padding: '7px 12px', fontSize: '12.5px' }}>
              <BI icon="link-45deg" /> Lier un compte existant
            </button>
          </div>
        </>
      )}
    </SectCard>
  );
}

// ── Vue mensuelle des CRA (admin) ─────────────────────────────────────────────
// GET /resources/cra/overview?month=YYYY-MM — l'état de saisie de TOUTES les
// ressources d'un coup, avec la liste de relance de fin de mois.
function CraOverview({ showNotification, crmOn, onFacturer, onSetStatus, onSignature, onOpenResource }) {
  const [month, setMonth] = useState(() => mKey(new Date()));
  const [data, setData] = useState(undefined); // undefined = chargement | null = route absente
  const load = useCallback(async () => {
    setData(undefined);
    try {
      const r = await fetch(`${BASE}/resources/cra/overview?month=${month}`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setData(null); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j.data || j);
    } catch (e) { showNotification?.(`Erreur CRA du mois : ${e.message}`, 'error'); setData(null); }
  }, [month, showNotification]);
  useEffect(() => { load(); }, [load]);

  const ST = {
    absent: { k: 'bad', l: 'Rien saisi' },
    draft: { k: 'mut', l: 'Brouillon' },
    submitted: { k: 'warn', l: 'Reçu — à valider' },
    signature_en_cours: { k: 'warn', l: 'Signature en cours' },
    client_validated: { k: 'ok', l: 'Validé client' },
    valide_interne: { k: 'ok', l: 'Prêt compta' },
  };
  const resume = data?.resume || {};
  const lignes = data?.lignes || [];

  // Validation compta depuis la ligne — même confirmation que le tiroir.
  const validerCompta = async (l) => {
    const ok = await confirmErpia({
      title: 'Valider pour la compta',
      message: `Valider définitivement le CRA ${mLabel(month)} de ${nameOf(l)} ?`,
      detail: 'Le mois est figé : c’est cette version qui sert à la facturation et qui alimentera la paie (Billy).',
      confirmLabel: 'Valider pour la compta', cancelLabel: 'Annuler', tone: 'primary',
    });
    if (ok && await onSetStatus?.(l, 'valide_interne', month)) load();
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <button onClick={() => setMonth(shiftMonth(month, -1))} className="src-row-hover" style={{ ...GHOST_BTN, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="chevron-left" style={{ fontSize: '11px' }} /></button>
        <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)', textTransform: 'capitalize', minWidth: '150px', textAlign: 'center' }}>{mLabel(month)}</span>
        <button onClick={() => setMonth(shiftMonth(month, 1))} className="src-row-hover" style={{ ...GHOST_BTN, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="chevron-right" style={{ fontSize: '11px' }} /></button>
        <div style={{ flex: 1 }} />
        <button onClick={load} className="src-row-hover" style={GHOST_BTN}><BI icon="arrow-repeat" /> Rafraîchir</button>
      </div>

      {data === undefined && (
        <div style={{ ...CARD, padding: '28px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
          <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement…
        </div>
      )}
      {data === null && (
        <div style={{ ...CARD, padding: '20px', fontSize: '13px', color: 'var(--text-2)' }}>
          La vue mensuelle des CRA arrive côté serveur — rien à afficher pour l'instant.
        </div>
      )}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '12px', marginBottom: '14px' }}>
            {[['Suivies', resume.total], ['Rien saisi', resume.absents], ['Brouillons', resume.brouillons], ['Soumis', resume.soumis], ['Validés client', resume.valides_client],
              // Compteurs du circuit étendu — rendus seulement si le serveur les publie.
              ...(resume.signatures_en_cours != null ? [['Signatures en cours', resume.signatures_en_cours]] : []),
              ...(resume.prets_compta != null ? [['Prêts compta', resume.prets_compta]] : [])].map(([l, v]) => (
              <div key={l} style={{ ...CARD, padding: '14px 16px' }}>
                <div style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>{l}</div>
                <div className="ats-mono" style={{ marginTop: '4px', fontSize: '24px', fontWeight: 500, color: l === 'Rien saisi' && Number(v) > 0 ? 'var(--brand)' : 'var(--text-strong)' }}>{v ?? '—'}</div>
              </div>
            ))}
          </div>

          {(resume.a_relancer || []).length > 0 && (
            <div style={{ marginBottom: '14px', padding: '13px 16px', borderRadius: '11px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.35)' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#b45309' }}><BI icon="bell" /> À relancer avant clôture ({resume.a_relancer.length})</div>
              <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--text)' }}>{resume.a_relancer.join(' · ')}</div>
            </div>
          )}

          <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
            {lignes.length === 0 && <div style={{ padding: '20px', fontSize: '13px', color: 'var(--text-faint)' }}>Aucune ressource suivie ce mois.</div>}
            {lignes.map((l, i) => {
              const st = ST[l.cra?.status] || ST.draft;
              return (
                <div key={l.resource_id ?? i} onClick={() => onOpenResource?.(l.resource_id)} className="src-row-hover"
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderTop: i === 0 ? 'none' : '1px solid var(--hair)', cursor: 'pointer' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, flex: 'none' }}>{initialsOf(l)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(l)}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[l.mission_titre, l.client].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                  <span className="ats-mono" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', flex: 'none' }}>{l.cra?.total != null ? `${l.cra.total} j` : '—'}</span>
                  <Chip kind={st.k}>{st.l}</Chip>
                  {l.cra?.status === 'submitted' && (
                    <button onClick={e => { e.stopPropagation(); Promise.resolve(onSignature?.(l, month)).then(ok => { if (ok) load(); }); }} className="src-row-hover"
                      title="Le client reçoit le relevé à signer (signd) — validation automatique à la signature."
                      style={{ ...GHOST_BTN, padding: '7px 11px', fontSize: '12px' }}>
                      <BI icon="pen" /> Faire signer (signd)
                    </button>
                  )}
                  {l.cra?.status === 'client_validated' && (
                    <button onClick={e => { e.stopPropagation(); validerCompta(l); }} className="src-row-hover"
                      title="Fige le mois pour la facturation et, demain, la paie (Billy)."
                      style={{ ...GHOST_BTN, padding: '7px 11px', fontSize: '12px' }}>
                      <BI icon="check2-all" /> Valider compta
                    </button>
                  )}
                  {crmOn && (l.cra?.status === 'client_validated' || l.cra?.status === 'valide_interne') && (
                    <button onClick={e => { e.stopPropagation(); Promise.resolve(onFacturer?.(l.resource_id, month)).then(load); }}
                      style={l.cra.status === 'valide_interne'
                        ? { ...PRIMARY_BTN, padding: '7px 11px', fontSize: '12px', flex: 'none' }
                        : { ...GHOST_BTN, padding: '7px 11px', fontSize: '12px' }}
                      title={l.cra.status === 'valide_interne' ? undefined : 'Conseillé : valider pour la compta d’abord.'}>
                      <BI icon="receipt" /> Facturer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Assistant « Créer une mission » ──────────────────────────────────────────
// Trois étapes guidées depuis la fiche ressource : le client, les conditions,
// puis un récapitulatif avant création. Réutilise la même route que le CRM
// (POST /missions) pour hériter des mêmes contrôles — la mission apparaît
// ensuite dans CRM → Missions et alimente les CRA et la facturation.
const MISSION_WIZ_STATUTS = [['active', 'Active'], ['draft', 'À préparer']];
function MissionAssistant({ r, onClose, showNotification, onDone }) {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState(null);        // null = chargement
  const [clientsAbsents, setClientsAbsents] = useState(false);
  const [cq, setCq] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(() => ({
    client: r.client || '', client_id: '',
    tjm: '', taux_activite: r.taux_activite != null ? String(r.taux_activite) : '100',
    date_debut: new Date().toISOString().slice(0, 10), date_fin: '',
    titre: '', statut: 'active',
  }));

  useEffect(() => {
    (async () => {
      try {
        const rr = await fetch(`${BASE}/clients`, { headers: authH() });
        if (rr.status === 404 || rr.status === 501) { setClientsAbsents(true); setClients([]); return; }
        const j = await rr.json().catch(() => ({}));
        const list = j.data || j.items || (Array.isArray(j) ? j : []);
        const arr = Array.isArray(list) ? list : [];
        setClients(arr);
        // Pré-sélection : la fiche porte déjà un nom de client connu.
        if (r.client) {
          const hit = arr.find(c => (c.nom || '').trim().toLowerCase() === r.client.trim().toLowerCase());
          if (hit) setF(p => ({ ...p, client: hit.nom, client_id: hit.id }));
        }
      } catch { setClients([]); }
    })();
  }, [r.client]);

  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filtres = (clients || []).filter(c => !cq.trim() || norm(c.nom).includes(norm(cq)));
  const exact = (clients || []).some(c => norm(c.nom) === norm(cq.trim()));

  const proposeTitre = () => (r.mission_titre ? `${r.mission_titre} — ${f.client}` : `${nameOf(r)} — ${f.client}`).replace(/ — $/, '').trim();
  const suivant = () => {
    if (step === 1) {
      if (!f.client.trim()) { showNotification?.('Choisissez ou saisissez le client.', 'error'); return; }
      setStep(2);
    } else if (step === 2) {
      setF(p => ({ ...p, titre: p.titre || proposeTitre() }));
      setStep(3);
    }
  };

  const creer = async () => {
    if (!f.titre.trim()) { showNotification?.('Le titre de la mission est requis.', 'error'); return; }
    setBusy(true);
    try {
      const body = {
        titre: f.titre.trim(),
        client: f.client.trim(),
        client_id: f.client_id || undefined,
        resource_id: Number(r.id),
        tjm: f.tjm !== '' ? Number(f.tjm) : null,
        date_debut: f.date_debut || null,
        date_fin: f.date_fin || null,
        taux_activite: f.taux_activite !== '' ? Number(f.taux_activite) : null,
        statut: f.statut,
      };
      const rr = await fetch(`${BASE}/missions`, { method: 'POST', headers: jsonH(), body: JSON.stringify(body) });
      if (rr.status === 404 || rr.status === 501) { showNotification?.('La création de mission arrive côté serveur — rien n\'a été enregistré.', 'error'); return; }
      const j = await rr.json().catch(() => ({}));
      if (!(rr.status === 201 || rr.ok) || j.success === false) throw new Error(j.error || j.message || `HTTP ${rr.status}`);
      showNotification?.(`Mission « ${f.titre.trim()} » créée pour ${nameOf(r)} — visible dans CRM → Missions.`, 'success');
      onDone?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const fld = (label, node) => <div><div style={{ ...LBL, marginBottom: '5px' }}>{label}</div>{node}</div>;
  const ETAPES = ['Client', 'Conditions', 'Récapitulatif'];
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 71, width: 'min(520px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Créer une mission — {nameOf(r)}</div>
        <div style={{ display: 'flex', gap: '6px', margin: '10px 0 16px' }}>
          {ETAPES.map((l, i) => (
            <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 600,
              background: step === i + 1 ? 'var(--accent)' : step > i + 1 ? 'var(--accent-soft-bg)' : 'var(--code-bg)',
              color: step === i + 1 ? 'var(--accent-fg)' : step > i + 1 ? 'var(--accent-soft-fg)' : 'var(--text-3)' }}>
              {step > i + 1 ? <BI icon="check-lg" style={{ fontSize: '10px' }} /> : <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px' }}>{i + 1}</span>}{l}
            </span>
          ))}
        </div>

        {step === 1 && (
          <div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '10px' }}>Pour quel client cette mission ? Choisissez dans la liste ou saisissez un nouveau nom.</div>
            {clientsAbsents && <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '8px' }}>La liste des clients arrive côté serveur — le nom saisi sera utilisé tel quel.</div>}
            <input value={cq} onChange={e => setCq(e.target.value)} placeholder="Rechercher ou saisir un client…" style={INPUT} autoFocus />
            {clients === null ? (
              <div style={{ padding: '14px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '12.5px' }}><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement…</div>
            ) : (
              <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {filtres.map(c => {
                  const sel = String(f.client_id) === String(c.id) && f.client === c.nom;
                  return (
                    <button key={c.id} onClick={() => setF(p => ({ ...p, client: c.nom, client_id: c.id }))} className="src-row-hover"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', padding: '9px 12px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                        border: sel ? '1px solid var(--accent)' : '1px solid var(--hair)', background: sel ? 'var(--accent-soft-bg)' : 'transparent', color: 'var(--text-strong)' }}>
                      <BI icon={sel ? 'check-circle-fill' : 'building'} style={{ fontSize: '12px', color: sel ? 'var(--accent)' : 'var(--text-3)' }} />{c.nom}
                    </button>
                  );
                })}
                {cq.trim() !== '' && !exact && (
                  <button onClick={() => setF(p => ({ ...p, client: cq.trim(), client_id: '' }))} className="src-row-hover"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', padding: '9px 12px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                      border: f.client === cq.trim() && !f.client_id ? '1px solid var(--accent)' : '1px dashed var(--hair)', background: f.client === cq.trim() && !f.client_id ? 'var(--accent-soft-bg)' : 'transparent', color: 'var(--text-strong)' }}>
                    <BI icon="plus-circle" style={{ fontSize: '12px', color: 'var(--text-3)' }} />Nouveau client « {cq.trim()} »
                  </button>
                )}
                {filtres.length === 0 && cq.trim() === '' && <div style={{ padding: '10px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun client pour l'instant — saisissez un nom ci-dessus.</div>}
              </div>
            )}
            {f.client && <div style={{ marginTop: '10px', fontSize: '12.5px', color: 'var(--text-2)' }}>Client retenu : <strong style={{ color: 'var(--text-strong)' }}>{f.client}</strong>{!f.client_id && f.client ? ' (nouveau nom, utilisé tel quel)' : ''}</div>}
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '10px' }}>Les conditions de la mission. Tout reste modifiable ensuite dans CRM → Missions.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '11px' }}>
              {fld('TJM (CHF/jour)', <input value={f.tjm} onChange={e => setF(p => ({ ...p, tjm: e.target.value }))} type="number" min="0" style={{ ...INPUT, fontFamily: "'IBM Plex Mono',monospace" }} placeholder="950" autoFocus />)}
              {fld('Taux d\'activité (%)', <input value={f.taux_activite} onChange={e => setF(p => ({ ...p, taux_activite: e.target.value }))} type="number" min="10" max="100" style={{ ...INPUT, fontFamily: "'IBM Plex Mono',monospace" }} />)}
              {fld('Début', <input value={f.date_debut} onChange={e => setF(p => ({ ...p, date_debut: e.target.value }))} type="date" style={INPUT} />)}
              {fld('Fin (facultatif)', <input value={f.date_fin} onChange={e => setF(p => ({ ...p, date_fin: e.target.value }))} type="date" style={INPUT} />)}
            </div>
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-3)' }}>Le TJM peut rester vide et être complété plus tard — la facturation depuis le CRA en aura besoin.</div>
          </div>
        )}

        {step === 3 && (
          <div>
            {fld('Titre de la mission *', <input value={f.titre} onChange={e => setF(p => ({ ...p, titre: e.target.value }))} style={INPUT} autoFocus />)}
            <div style={{ marginTop: '11px' }}>{fld('Statut au démarrage', (
              <div style={{ display: 'inline-flex', gap: '4px', padding: '3px', borderRadius: '9px', background: 'var(--code-bg)' }}>
                {MISSION_WIZ_STATUTS.map(([k, l]) => (
                  <button key={k} onClick={() => setF(p => ({ ...p, statut: k }))}
                    style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, background: f.statut === k ? 'var(--accent)' : 'transparent', color: f.statut === k ? 'var(--accent-fg)' : 'var(--text-2)' }}>{l}</button>
                ))}
              </div>
            ))}</div>
            <div style={{ ...CARD, borderRadius: '10px', padding: '12px 14px', marginTop: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
              {[['Ressource', nameOf(r)], ['Client', f.client || '—'],
                ['TJM', f.tjm !== '' ? `${Number(f.tjm).toLocaleString('fr-CH')} CHF/j` : 'à compléter'],
                ['Taux d\'activité', f.taux_activite !== '' ? `${f.taux_activite}%` : '—'],
                ['Début', fmtDate(f.date_debut) || '—'], ['Fin', fmtDate(f.date_fin) || '—']].map(([k, v]) => (
                <div key={k}><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{k}</div><div style={{ marginTop: '2px', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{v}</div></div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
          {step > 1 && <button onClick={() => setStep(step - 1)} className="src-row-hover" style={GHOST_BTN}><BI icon="chevron-left" /> Retour</button>}
          {step < 3
            ? <button onClick={suivant} style={PRIMARY_BTN}>Continuer <BI icon="chevron-right" /></button>
            : <button onClick={creer} disabled={busy} style={{ ...PRIMARY_BTN, opacity: busy ? 0.6 : 1 }}><BI icon={busy ? 'arrow-repeat' : 'check-lg'} /> Créer la mission</button>}
        </div>
      </div>
    </>
  );
}

// ── Vue détaillée pleine page (⤢ depuis le drawer) ───────────────────────────
function ResourceDetail({ r, onBack, showNotification, onSaved }) {
  const { confirm, confirmElement } = useConfirm();
  const admin = isAoAdmin(APIService.getCurrentUser());
  const [suppr, setSuppr] = useState(false);
  // Suppression : douce par défaut (fiche retirée, historique conservé, compte
  // révoqué, email libéré) ; définitive derrière une SECONDE confirmation.
  // Les messages du serveur (réponse et 409) s'affichent tels quels.
  const supprimer = async (definitif) => {
    if (definitif) {
      if (!(await confirm({ title: 'Supprimer définitivement', message: `Supprimer DÉFINITIVEMENT ${nameOf(r)} ?`, detail: 'Efface aussi les CRA, congés, frais et documents. La suppression simple (fiche retirée, historique conservé) suffit dans la plupart des cas.', confirmLabel: 'Continuer' }))) return;
      if (!(await confirm({ title: 'Dernière vérification', message: `Confirmer l'effacement définitif de ${nameOf(r)} ?`, detail: 'Il n\'y a pas de corbeille. Un CRA déjà facturé ou validé client bloquera cette suppression (pièces comptables).', confirmLabel: 'Supprimer définitivement' }))) return;
    } else if (!(await confirm({ title: 'Supprimer la ressource', message: `Supprimer la fiche de ${nameOf(r)} ?`, detail: 'La fiche sort des listes, l\'historique CRA/congés/frais est conservé, le compte de connexion est révoqué et son email redevient libre.', confirmLabel: 'Supprimer la fiche' }))) return;
    setSuppr(true);
    try {
      const rr = await fetch(`${BASE}/resources/${r.id}${definitif ? '?definitif=1' : ''}`, { method: 'DELETE', headers: authH() });
      const j = await rr.json().catch(() => ({}));
      if (rr.status === 409) { showNotification?.(j.error || j.message || 'Suppression refusée.', 'error'); return; }
      if (!rr.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${rr.status}`);
      showNotification?.(j.message || 'Ressource supprimée.', 'success');
      onSaved?.(); onBack?.();
    } catch (e) { showNotification?.(`Suppression impossible : ${e.message}`, 'error'); }
    finally { setSuppr(false); }
  };
  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {confirmElement}
      <button onClick={onBack} className="src-row-hover" style={{ ...GHOST_BTN, marginBottom: '16px' }}><BI icon="chevron-left" /> Ressources</button>
      <div style={{ ...CARD, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '17px', fontWeight: 600, flex: 'none' }}>{initialsOf(r)}</div>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{nameOf(r)}</div>
          <div style={{ marginTop: '2px', fontSize: '13px', color: 'var(--text-2)' }}>{[r.mission_titre, r.client].filter(Boolean).join(' · ') || '—'}</div>
        </div>
        {r.taux_activite != null && <Chip kind="mut">{r.taux_activite}%</Chip>}
        {r.statut && <Chip kind="ok">{r.statut}</Chip>}
        {r.user_username ? <Chip kind="mut"><BI icon="person-check" style={{ fontSize: '10px' }} /> {r.user_username}</Chip> : <Chip kind="mut" style={{ opacity: 0.6 }}>sans compte</Chip>}
        {admin && (
          <span style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => supprimer(false)} disabled={suppr} className="src-row-hover"
              title="La fiche sort des listes ; l'historique CRA/congés/frais est conservé ; le compte est révoqué et l'email libéré."
              style={{ ...GHOST_BTN, color: 'var(--danger-fg, #cf3b32)' }}><BI icon="trash3" /> Supprimer</button>
            <button onClick={() => supprimer(true)} disabled={suppr} className="src-row-hover"
              title="Efface aussi les CRA, congés, frais et documents — double confirmation demandée."
              style={{ ...GHOST_BTN, fontSize: '11.5px', opacity: 0.8 }}>Supprimer définitivement…</button>
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '14px', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <AccessSection r={r} showNotification={showNotification} onSaved={onSaved} />
          <AdminSection r={r} showNotification={showNotification} onSaved={onSaved} />
          <FraisSection rid={r.id} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <FormationSection rid={r.id} showNotification={showNotification} />
          <ActionsSection rid={r.id} name={nameOf(r)} showNotification={showNotification} />
        </div>
      </div>
    </div>
  );
}

// ── Modale « Ajouter une ressource » ─────────────────────────────────────────
// Deux portes d'entrée (revoir 29) : conversion d'un candidat du vivier
// (Express immédiat OU workflow complet 3 étapes : Infos pré-remplies →
// Contrat généré pré-rempli envoyé en signature → Documents guidés) et le
// formulaire direct « Nouvelle ressource ». Les deux modes créent la même
// fiche — tout reste modifiable ensuite.
const CONTRAT_TYPES = ['Mission', 'CDI', 'CDD', 'Portage'];
const DOC_KEYS = [
  ['contrat_signe', 'Contrat signé'],
  ['permis', 'Permis de travail / CNI'],
  ['avs', 'Attestation AVS'],
  ['iban', 'Coordonnées bancaires (IBAN)'],
];
const Seg = ({ options, value, onChange }) => (
  <div style={{ display: 'inline-flex', gap: '4px', padding: '3px', borderRadius: '9px', background: 'var(--code-bg)' }}>
    {options.map(o => (
      <button key={o} onClick={() => onChange(o)}
        style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, background: value === o ? 'var(--accent)' : 'transparent', color: value === o ? 'var(--accent-fg)' : 'var(--text-2)' }}>{o}</button>
    ))}
  </div>
);

function AddResourceModal({ onClose, showNotification, onCreated }) {
  // Étapes : source (candidat vs vierge) → form (direct) OU choice → infos → contrat → docs
  const [step, setStep] = useState('source');
  const [cand, setCand] = useState(null);      // candidat sélectionné (conversion)
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null); // null = rien cherché
  const [searching, setSearching] = useState(false);
  const [f, setF] = useState({ nom: '', prenom: '', client: '', mission_titre: '', date_debut: '', date_fin: '', taux_activite: '', user_id: '', contrat_type: 'Mission' });
  const [contractGen, setContractGen] = useState(null); // { ok, file_name, url } | { missing: true }
  const [genBusy, setGenBusy] = useState(false);
  const [docs, setDocs] = useState({});        // { [key]: File }
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));
  const docRefs = useRef({});

  // Recherche candidats (annuaire vivier — même endpoint que l'écran Candidats)
  useEffect(() => {
    if (step !== 'source' || !search.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${BASE}/candidats/vue?search=${encodeURIComponent(search.trim())}&limit=8&page=1`, { headers: authH() });
        const j = await r.json().catch(() => ({}));
        const arr = j.data || j.candidats || j.rows || (Array.isArray(j) ? j : []);
        setResults(Array.isArray(arr) ? arr : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, step]);

  const pickCandidate = (c) => {
    setCand(c);
    setF(prev => ({
      ...prev,
      prenom: c.prenom || '', nom: c.nom || '',
      mission_titre: c.poste || c.titre_poste || c.titre || prev.mission_titre,
      taux_activite: prev.taux_activite || '100',
    }));
    setStep('choice');
  };

  const contractFileName = () => {
    const p = (f.prenom || '').trim().replace(/\s+/g, '_');
    const n = (f.nom || '').trim().toUpperCase().replace(/\s+/g, '_');
    return `Contrat_${f.contrat_type}_${p}_${n}.pdf`.replace(/_+/g, '_');
  };

  // Génération du contrat pré-rempli (workflow complet, étape 2/3) — tolérant :
  // route absente → l'étape se passe, la fiche se crée quand même.
  const generateContract = async () => {
    setGenBusy(true);
    try {
      const r = await fetch(`${BASE}/resources/contract`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({
          candidate_id: cand?.id, type: f.contrat_type,
          prenom: f.prenom.trim(), nom: f.nom.trim(),
          client: f.client.trim() || undefined, mission_titre: f.mission_titre.trim() || undefined,
          taux_activite: f.taux_activite ? Number(f.taux_activite) : undefined,
        }),
      });
      if (r.status === 404 || r.status === 501) { setContractGen({ missing: true }); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const d = j.data || j;
      setContractGen({ ok: true, file_name: d.file_name || contractFileName(), url: d.url || d.preview_url || null });
    } catch (e) { showNotification?.('Erreur génération du contrat : ' + e.message, 'error'); }
    finally { setGenBusy(false); }
  };

  const createResource = async (mode) => {
    if (!f.nom.trim()) { showNotification?.('Le nom est requis.', 'error'); return; }
    if (mode === 'form' && !f.client.trim()) { showNotification?.('Nom et client sont requis.', 'error'); return; }
    setSaving(true);
    try {
      const body = { nom: f.nom.trim(), prenom: f.prenom.trim() || undefined, mission_titre: f.mission_titre.trim() || undefined };
      if (f.client.trim()) body.client = f.client.trim();
      if (f.date_debut) body.date_debut = f.date_debut;
      if (f.date_fin) body.date_fin = f.date_fin;
      if (f.taux_activite) body.taux_activite = Number(f.taux_activite);
      if (f.user_id) body.user_id = Number(f.user_id);
      if (mode === 'complet') {
        body.contrat_type = f.contrat_type;
        if (contractGen?.ok) body.contract_generated = true;             // le back envoie en signature (signd)
      }
      const convertingCandidate = cand?.id != null;
      const besoinId = cand?.besoin_id ?? cand?.besoinId ?? cand?.positionnement?.besoin_id;
      const endpoint = convertingCandidate ? '/resources/from-candidate' : '/resources';
      const createBody = convertingCandidate
        ? { candidate_id: cand.id, ...(besoinId != null ? { besoin_id: besoinId } : {}) }
        : body;
      const r = await fetch(`${BASE}${endpoint}`, { method: 'POST', headers: jsonH(), body: JSON.stringify(createBody) });
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const result = j.data || j;
      const resId = result.id ?? result.resource?.id ?? result.resource_id ?? result.ressource_id ?? j.resource_id ?? null;

      // L'endpoint de conversion reste volontairement limité à candidate_id/besoin_id.
      // Les compléments du formulaire sont appliqués ensuite sur la fiche retournée.
      if (convertingCandidate && resId != null) {
        const updateBody = { ...body };
        delete updateBody.contract_generated;
        const updateResponse = await fetch(`${BASE}/resources/${resId}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(updateBody) });
        if (!updateResponse.ok) showNotification?.('Ressource convertie, mais certaines informations complémentaires n’ont pas pu être enregistrées.', 'error');
      }

      // Documents guidés (workflow complet) — import après création, tolérant
      const picked = Object.entries(docs).filter(([, file]) => file);
      let docsSent = 0, docsRouteMissing = false;
      if (mode === 'complet' && picked.length && resId != null) {
        for (const [key, file] of picked) {
          try {
            const fd = new FormData();
            fd.append('document', file);
            fd.append('label', DOC_KEYS.find(([k]) => k === key)?.[1] || key);
            const dr = await fetch(`${BASE}/resources/${resId}/documents`, { method: 'POST', headers: authH(), body: fd });
            if (dr.status === 404 || dr.status === 501) { docsRouteMissing = true; break; }
            if (dr.ok) docsSent++;
          } catch { /* fiche créée quand même */ }
        }
      }

      const name = `${f.prenom} ${f.nom}`.trim();
      const reused = convertingCandidate && result.created === false;
      showNotification?.(
        reused ? (result.message || j.message || 'Fiche ressource existante réutilisée.')
          : mode === 'express' ? `${name} créé en express — contrat et documents à compléter depuis la fiche.`
            : mode === 'complet' ? `${name} créé${contractGen?.ok ? ' — contrat envoyé en signature' : ''}${docsSent ? ` · ${docsSent} document(s) importé(s)` : ''}.`
              : (j.message || `Ressource ${name} créée${f.user_id ? ' — compte lié (rôle ressource posé)' : ''}.`),
        'success');
      if (docsRouteMissing) showNotification?.('L\'import des documents arrive côté serveur — fichiers non envoyés, la checklist reste sur la fiche.', 'error');
      onCreated?.();
    } catch (e) { showNotification?.('Erreur création : ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  const field = (label, k, props = {}) => (
    <div>
      <div style={{ ...LBL, marginBottom: '6px' }}>{label}</div>
      <input value={f[k]} onChange={set(k)} style={INPUT} {...props} />
    </div>
  );
  const choiceCard = (extra) => ({ flex: 1, minWidth: '200px', textAlign: 'left', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--surface)', padding: '16px', cursor: 'pointer', fontFamily: 'inherit', position: 'relative', ...extra });
  const tileIcon = { width: '34px', height: '34px', borderRadius: '9px', background: 'var(--code-bg)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' };
  const stepNote = step === 'choice' ? 'Les deux modes créent la même fiche — tout reste modifiable ensuite.'
    : step === 'infos' ? 'Étape 1/3 — informations contractuelles pré-remplies depuis la fiche candidat.'
      : step === 'contrat' ? 'Étape 2/3 — le contrat est généré pré-rempli, modifiable avant signature.'
        : step === 'docs' ? 'Étape 3/3 — documents optionnels, la checklist reste visible sur la fiche.'
          : null;
  const title = step === 'form' ? 'Nouvelle ressource'
    : step === 'source' ? 'Ajouter une ressource'
      : `Convertir ${(f.prenom || cand?.prenom || '')} ${(f.nom || cand?.nom || '')}`.trim();

  const back = () => setStep(step === 'docs' ? 'contrat' : step === 'contrat' ? 'infos' : step === 'infos' ? 'choice' : 'source');

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 71, width: 'min(640px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          {step !== 'source' && (
            <button onClick={back} disabled={saving} className="src-row-hover" style={{ ...GHOST_BTN, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="chevron-left" style={{ fontSize: '12px' }} /></button>
          )}
          <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', flex: 1 }}>{title}</span>
          <button onClick={onClose} className="src-row-hover" style={{ ...GHOST_BTN, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" style={{ fontSize: '12px' }} /></button>
        </div>
        {stepNote && <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '14px' }}>{stepNote}</div>}
        {!stepNote && <div style={{ marginBottom: '12px' }} />}

        {/* ── Porte d'entrée : candidat du vivier ou fiche vierge ── */}
        {step === 'source' && (
          <>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div style={choiceCard({ cursor: 'default' })}>
                <div style={tileIcon}><BI icon="person-up" /></div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Depuis le vivier</div>
                <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '3px', lineHeight: 1.5 }}>Transformez un candidat en ressource — identité et mission pré-remplies depuis sa fiche.</div>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un candidat (nom, prénom…)" autoFocus
                  style={{ ...INPUT, marginTop: '10px' }} />
                {searching && <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '8px' }}><BI icon="arrow-repeat" style={{ animation: 'ats-spin .7s linear infinite', display: 'inline-block' }} /> Recherche…</div>}
                {results && !searching && (
                  results.length === 0 ? <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '8px' }}>Aucun candidat ne correspond.</div>
                    : (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {results.map(c => (
                          <button key={c.id} onClick={() => pickCandidate(c)} className="src-row-hover"
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '9px', border: '1px solid var(--hair)', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                            <span className="ats-mono" style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, flex: 'none' }}>
                              {`${(c.prenom || '?')[0] || ''}${(c.nom || '?')[0] || ''}`.toUpperCase()}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{`${c.prenom || ''} ${c.nom || ''}`.trim() || 'Sans nom'}</span>
                              <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.poste || c.titre_poste || c.localisation || c.email || '—'}</span>
                            </span>
                            <BI icon="chevron-right" style={{ fontSize: '11px', color: 'var(--text-faint)' }} />
                          </button>
                        ))}
                      </div>
                    )
                )}
              </div>
              <button onClick={() => setStep('form')} className="src-row-hover" style={choiceCard()}>
                <div style={tileIcon}><BI icon="person-plus" /></div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Nouvelle ressource</div>
                <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '3px', lineHeight: 1.5 }}>Formulaire direct — pour une personne qui n'est pas dans le vivier candidats.</div>
              </button>
            </div>
          </>
        )}

        {/* ── Conversion : choix Express / Workflow complet ── */}
        {step === 'choice' && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => createResource('express')} disabled={saving} className="src-row-hover" style={choiceCard()}>
              <div style={tileIcon}><BI icon="skip-forward" /></div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Express</div>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '3px', lineHeight: 1.5 }}>La ressource est créée immédiatement — sans contrat ni document. Tout se complète plus tard depuis la fiche.</div>
              <div className="ats-mono" style={{ fontSize: '10.5px', color: 'var(--text-faint)', marginTop: '10px', letterSpacing: '0.05em' }}>~10 SECONDES</div>
            </button>
            <button onClick={() => setStep('infos')} disabled={saving} className="src-row-hover" style={choiceCard({ borderColor: 'var(--accent)' })}>
              <span style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: '5px', background: 'var(--accent)', color: 'var(--accent-fg)' }}>RECOMMANDÉ</span>
              <div style={tileIcon}><BI icon="diagram-3" /></div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Workflow complet</div>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '3px', lineHeight: 1.5 }}>Contrat généré pré-rempli (CDI, CDD, mission…), documents importés, invitation envoyée à la ressource.</div>
              <div className="ats-mono" style={{ fontSize: '10.5px', color: 'var(--text-faint)', marginTop: '10px', letterSpacing: '0.05em' }}>3 ÉTAPES · ~2 MIN</div>
            </button>
          </div>
        )}

        {/* ── Étape 1/3 : infos contractuelles ── */}
        {step === 'infos' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {field('Prénom', 'prenom')}
              {field('Nom *', 'nom')}
              {field('Client', 'client', { placeholder: 'UBP' })}
              {field('Rôle / mission', 'mission_titre', { placeholder: 'Data Engineer' })}
              {field('Début', 'date_debut', { type: 'date' })}
              {field('Fin', 'date_fin', { type: 'date' })}
            </div>
            <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '14px' }}>
              <div>
                <div style={{ ...LBL, marginBottom: '6px' }}>Taux d'activité</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Seg options={['100', '80', '60']} value={String(f.taux_activite)} onChange={v => setF(p => ({ ...p, taux_activite: v }))} />
                  <input value={f.taux_activite} onChange={set('taux_activite')} type="number" min="10" max="100" style={{ ...INPUT, width: '74px' }} />
                  <span style={{ fontSize: '12.5px', color: 'var(--text-3)' }}>%</span>
                </div>
              </div>
              <div>
                <div style={{ ...LBL, marginBottom: '6px' }}>Type de contrat</div>
                <Seg options={CONTRAT_TYPES} value={f.contrat_type} onChange={v => setF(p => ({ ...p, contrat_type: v }))} />
              </div>
            </div>
            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={back} className="src-row-hover" style={GHOST_BTN}>Retour</button>
              <button onClick={() => setStep('contrat')} disabled={!f.nom.trim()} style={{ ...PRIMARY_BTN, opacity: f.nom.trim() ? 1 : 0.5 }}>Continuer <BI icon="arrow-right" /></button>
            </div>
          </>
        )}

        {/* ── Étape 2/3 : contrat généré pré-rempli ── */}
        {step === 'contrat' && (
          <>
            <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', background: 'var(--surface)' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '10px' }}>Contrat {f.contrat_type} — pré-rempli depuis le dossier</div>
              {[['Collaborateur', `${f.prenom} ${f.nom}`.trim() || '—'], ['Fonction', f.mission_titre || '—'], ['Taux d\'activité', f.taux_activite ? `${f.taux_activite}%` : '—'], ['Rémunération', 'Selon mission (CRM)']].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12.5px', padding: '5px 0', borderBottom: '1px solid var(--hair)' }}>
                  <span style={{ color: 'var(--text-3)' }}>{k}</span><span style={{ color: 'var(--text)', fontWeight: 600 }}>{v}</span>
                </div>
              ))}
              {!contractGen?.ok && !contractGen?.missing && (
                <button onClick={generateContract} disabled={genBusy} style={{ ...PRIMARY_BTN, marginTop: '14px', opacity: genBusy ? 0.6 : 1 }}>
                  <BI icon={genBusy ? 'arrow-repeat' : 'file-earmark-plus'} style={genBusy ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {genBusy ? 'Génération…' : 'Générer le contrat pré-rempli'}
                </button>
              )}
              {contractGen?.ok && (
                <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '11px', padding: '11px 13px', borderRadius: '9px', background: 'var(--code-bg)', flexWrap: 'wrap' }}>
                  <BI icon="file-earmark-pdf" style={{ fontSize: '16px', color: 'var(--text-2)' }} />
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <div className="ats-mono" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-strong)' }}>{contractGen.file_name}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>Généré — sera envoyé en signature électronique à la création</div>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'rgba(22,163,74,0.14)', color: '#16a34a' }}>Généré</span>
                  {contractGen.url && <button onClick={() => window.open(contractGen.url, '_blank')} className="src-row-hover" style={GHOST_BTN}><BI icon="eye" /> Aperçu</button>}
                </div>
              )}
              {contractGen?.missing && (
                <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-3)', padding: '10px 12px', borderRadius: '9px', background: 'var(--code-bg)' }}>
                  <BI icon="info-circle" style={{ marginRight: '6px' }} />La génération de contrat arrive côté serveur — vous pouvez passer cette étape, la fiche se crée quand même.
                </div>
              )}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={back} className="src-row-hover" style={GHOST_BTN}>Retour</button>
              <button onClick={() => setStep('docs')} style={PRIMARY_BTN}>Continuer <BI icon="arrow-right" /></button>
            </div>
          </>
        )}

        {/* ── Étape 3/3 : documents guidés ── */}
        {step === 'docs' && (
          <>
            <div style={{ ...LBL, marginBottom: '10px' }}>Documents guidés <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>— requis pour un dossier complet, importables plus tard</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {DOC_KEYS.map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 13px', borderRadius: '10px', border: '1px solid var(--hair)', background: 'var(--surface)' }}>
                  <BI icon={docs[key] ? 'check-circle-fill' : 'circle'} style={{ color: docs[key] ? '#16a34a' : 'var(--text-faint)', fontSize: '14px', flex: 'none' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{label}</div>
                    {docs[key] && <div className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{docs[key].name}</div>}
                  </div>
                  <input ref={el => { docRefs.current[key] = el; }} type="file" style={{ display: 'none' }}
                    onChange={e => { const file = e.target.files?.[0]; if (file) setDocs(p => ({ ...p, [key]: file })); e.target.value = ''; }} />
                  {docs[key]
                    ? <button onClick={() => setDocs(p => ({ ...p, [key]: null }))} className="src-row-hover" style={GHOST_BTN}>Retirer</button>
                    : <button onClick={() => docRefs.current[key]?.click()} className="src-row-hover" style={GHOST_BTN}><BI icon="upload" /> Importer</button>}
                </div>
              ))}
            </div>
            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={back} disabled={saving} className="src-row-hover" style={GHOST_BTN}>Retour</button>
              <button onClick={() => createResource('complet')} disabled={saving} style={{ ...PRIMARY_BTN, opacity: saving ? 0.6 : 1 }}>
                <BI icon={saving ? 'arrow-repeat' : 'check-lg'} style={saving ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {saving ? 'Création…' : 'Créer la ressource'}
              </button>
            </div>
          </>
        )}

        {/* ── Formulaire direct (hors vivier) ── */}
        {step === 'form' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {field('Prénom', 'prenom', { placeholder: 'Geoffrey' })}
              {field('Nom *', 'nom', { placeholder: 'SIMON' })}
              {field('Client *', 'client', { placeholder: 'UBP' })}
              {field('Mission', 'mission_titre', { placeholder: 'Data Engineer' })}
              {field('Début', 'date_debut', { type: 'date' })}
              {field('Fin', 'date_fin', { type: 'date' })}
              {field('Taux d\'activité (%)', 'taux_activite', { type: 'number', min: 10, max: 100, placeholder: '100' })}
              {field('user_id à lier (optionnel)', 'user_id', { type: 'number', placeholder: 'lie le compte + rôle ressource' })}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={onClose} disabled={saving} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
              <button onClick={() => createResource('form')} disabled={saving} style={{ ...PRIMARY_BTN, opacity: saving ? 0.6 : 1 }}>
                <BI icon={saving ? 'arrow-repeat' : 'check-lg'} style={saving ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {saving ? 'Création…' : 'Créer la ressource'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
